/* AKINCI independent theme. No external runtime dependencies. */
(() => {
  'use strict';
  if (window.akinciThemeInitialized) return;
  window.akinciThemeInitialized = true;
  const text = JSON.parse(document.querySelector('[data-interface-text]')?.textContent || '{}');
  const products = new WeakMap();
  const dialogOrigins = new WeakMap();
  let cartBusy = false;
  let checkoutIntent = null;
  let pendingCart = Promise.resolve();
  const trackCart = (work) => { pendingCart = work.catch(() => {}); return work; };
  let cartUnreconciled = false;
  // Suggestions tapped while the bag was busy, waiting to be added in turn (product id -> 'drawer' | 'page').
  const shelfQueue = new Map();
  const root = document.body;
  let announceTimer = 0;
  const writeStatus = (message) => { const node = document.querySelector('[data-global-status]'); if (node) node.textContent = message; };
  const status = (message) => { clearTimeout(announceTimer); writeStatus(message); };
  // Clears the global status first and says the message a moment later, so a screen reader hears it even
  // when it repeats what was said last (the same missing file, the same refused type). '' only clears.
  const announce = (message) => { status(''); if (message) announceTimer = setTimeout(() => writeStatus(message), 50); };
  // The scroll lock is derived from what is actually open, never toggled blind. The previous
  // version removed it only from a 'close' event listener; where that event does not reach the
  // listener the class is never removed and the page stays scroll-locked for good, after the cart
  // drawer as much as the menu. Reproduced: close the menu, html keeps .dialog-open and
  // overflow:hidden with no dialog open. Now every close path syncs, and the event is a backstop.
  const syncDialogState = () => {
    document.documentElement.classList.toggle('dialog-open', !!document.querySelector('dialog[open]'));
    // A trigger that names its dialog reports whether it is open (the header's Menu button).
    for (const trigger of document.querySelectorAll('[data-open-dialog][aria-controls][aria-expanded]')) {
      trigger.setAttribute('aria-expanded', String(!!document.getElementById(trigger.getAttribute('aria-controls'))?.open));
    }
  };
  // Focus goes back to the control that opened the dialog, whatever the width: the Menu drawer can open
  // from 320 to 1139px (theme.css shows the Menu button there and nowhere else) and its button is the one
  // stored here, never one picked by width. That button can stop being shown while the drawer is open: it
  // is display:none from 1140px, so a drawer opened on a narrower window that is then widened (or on a
  // tablet turned to landscape) closed with focus dropped to <body> (QA 10 Oct, opened at 1100, closed at
  // 1280). A control that is not rendered ignores focus(), so when focus did not land and is nowhere useful
  // it goes to the first control after the opener in page order that takes it (in the header: the
  // wordmark, so the next Tab reaches the desktop menu as it would have from Menu), else the nearest one
  // before it. Focus another dialog or the shopper already moved somewhere else is left alone.
  const FOCUS_CONTROLS = 'a[href],button,input,select,textarea,summary,[tabindex]';
  const focusNearby = (origin) => {
    if (document.querySelector('dialog[open]')) return;
    const controls = [...document.querySelectorAll(FOCUS_CONTROLS)]
      .filter(control => control !== origin && !origin.contains(control) && !control.closest('dialog') && !control.disabled && control.tabIndex >= 0);
    const split = controls.findIndex(control => origin.compareDocumentPosition(control) & Node.DOCUMENT_POSITION_FOLLOWING);
    const after = split < 0 ? [] : controls.slice(split);
    const before = (split < 0 ? controls : controls.slice(0, split)).reverse();
    for (const control of [...after.slice(0, 40), ...before.slice(0, 40)]) {
      control.focus();
      if (document.activeElement === control) return;
    }
  };
  const returnFocus = (dialog) => {
    const origin = dialogOrigins.get(dialog);
    if (origin && origin.isConnected) {
      origin.focus();
      const active = document.activeElement;
      if (active !== origin && (!active || active === document.body || dialog.contains(active))) focusNearby(origin);
    }
    else if (dialog.id === 'QuickSelect' && quickShelf) {
      // Opened from the bag's suggestions, and the bag was redrawn meanwhile (a refused add re-reads it):
      // back to the same suggestion when it is still offered, else to the bag's status line. Focus the
      // add already put on its new line stays there (the close event's backstop runs a task later).
      const active = document.activeElement;
      if (active && active !== document.body && !dialog.contains(active)) return;
      const area = shelfArea(quickShelf.where);
      focusTarget(area?.querySelector(`[data-cart-rec][data-product-id="${CSS.escape(String(quickShelf.rec))}"] button`) || area?.querySelector('[data-cart-status]'));
    }
  };
  const openDialog = (dialog, origin) => {
    if (!dialog || dialog.open) return;
    dialogOrigins.set(dialog, origin || document.activeElement);
    dialog.showModal();
    syncDialogState();
    dialog.querySelector('[data-close-dialog]')?.focus();
  };
  const closeDialog = (dialog) => {
    if (!dialog) return;
    dialog.close();
    syncDialogState();
    returnFocus(dialog);
  };
  for (const type of ['close', 'cancel']) {
    document.addEventListener(type, (event) => {
      if (!(event.target instanceof HTMLDialogElement)) return;
      // cancel fires before the dialog actually closes; settle first.
      setTimeout(() => { syncDialogState(); returnFocus(event.target); }, 0);
    }, true);
  }
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Tab') {
      const open = [...document.querySelectorAll('dialog[open]')];
      const dialog = open.find(item => item.contains(document.activeElement)) || open.at(-1);
      if (!dialog) return;
      const controls = [...dialog.querySelectorAll(FOCUS_CONTROLS)]
        .filter(control => !control.disabled && control.tabIndex >= 0 && control.getClientRects().length);
      const first = controls[0], last = controls.at(-1);
      if (first && event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (last && !event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      return;
    }
    if (event.key === 'Escape') setTimeout(syncDialogState, 0);
    if (event.key !== 'Escape' || document.querySelector('dialog[open]')) return;
    const details = event.target.closest('details[open]');
    if (details) { details.open = false; details.querySelector('summary')?.focus(); }
  });
  // The desktop menus (Gear, Collections, About, More) are native <details>: a click, Enter or Space
  // opens one, Escape closes it and puts focus back on its name (above). A click anywhere else, or
  // keyboard focus moving out of an open panel, closes it as well, so a panel never stays hanging
  // over the page after the shopper has moved on.
  document.addEventListener('click', (event) => {
    for (const details of document.querySelectorAll('.desktop-navigation details[open]')) if (!details.contains(event.target)) details.open = false;
  });
  document.addEventListener('focusout', (event) => {
    const details = event.target instanceof Element ? event.target.closest('.desktop-navigation details[open]') : null;
    if (details && event.relatedTarget instanceof Node && !details.contains(event.relatedTarget)) details.open = false;
  });

  // Phones and tablets: a card with a second photo is a two-photo swipe track (theme.css block 36). Its two bars
  // follow the swipe: data-card-photo on the card is 0 or 1. Scroll events do not bubble, so this listens in the
  // capture phase, for cards rendered later too (recommendations, bag suggestions).
  document.addEventListener('scroll', (event) => {
    const track = event.target instanceof Element && event.target.classList.contains('card-media--pair') ? event.target : null;
    const card = track && track.closest('.product-card');
    if (!card) return;
    const photo = String(track.clientWidth ? Math.min(1, Math.round(track.scrollLeft / track.clientWidth)) : 0);
    if (card.dataset.cardPhoto !== photo) card.dataset.cardPhoto = photo;
  }, { capture: true, passive: true });

  // An option control is either a <select> or a <fieldset> of radio chips. Everything below
  // reads through these three helpers so both render paths share one variant-resolution code path.
  const optionControls = (product) => [...product.querySelectorAll('[data-option-index]')];
  const readOption = (control) => control.tagName === 'SELECT' ? control.value : (control.querySelector('input:checked')?.value || '');
  const readOptionId = (control) => control.tagName === 'SELECT'
    ? control.selectedOptions[0]?.dataset.optionValueId
    : control.querySelector('input:checked')?.dataset.optionValueId;
  const writeOption = (control, value) => {
    if (control.tagName === 'SELECT') { control.value = value; return; }
    const match = [...control.querySelectorAll('input')].find(input => input.value === value);
    if (match) match.checked = true;
  };
  // A thickness view (/collections/…?view=13mm) links here with ?thickness=13mm, and its cards hand the
  // same value to the selection dialog. Only an option named "…thickness…" is preset, only to a value
  // that can still be bought, and never over a choice already made; the size stays the shopper's.
  const thicknessHint = (product) => {
    const host = product.closest('[data-quick-select-content]');
    if (host) return host.dataset.thickness || '';
    return product.dataset.urlSync === 'true' ? new URLSearchParams(location.search).get('thickness') || '' : '';
  };
  const flat = value => String(value).toLowerCase().replace(/\s+/g, '');
  function presetThickness(product, wanted) {
    if (!wanted) return;
    optionControls(product).forEach((control) => {
      const name = (control.tagName === 'SELECT' ? product.querySelector(`label[for="${CSS.escape(control.id)}"]`) : control.querySelector('legend'))?.textContent || '';
      if (!/thickness/i.test(name) || readOption(control)) return;
      if (control.tagName === 'SELECT') {
        const option = [...control.options].find(item => item.value && flat(item.value) === flat(wanted));
        if (option) control.value = option.value;
        return;
      }
      const input = [...control.querySelectorAll('input')].find(item => flat(item.value) === flat(wanted) && !item.hasAttribute('data-sold-out'));
      if (input) input.checked = true;
    });
  }

  function showMedia(product, id, scroll = true) {
    const target = id && product.querySelector(`[data-media-id="${CSS.escape(String(id))}"]`);
    if (!target) return;
    product.querySelectorAll('[data-media-id]').forEach((item) => {
      const active = item.dataset.mediaId === String(id);
      item.classList.toggle('is-active', active);
      if (!active) item.querySelectorAll('video').forEach(video => video.pause());
    });
    product.querySelectorAll('[data-show-media]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.showMedia === String(id))));
    // On the phone breakpoint the stage is a horizontal snap track; move it to the chosen frame.
    const stage = product.querySelector('.gallery-stage');
    if (scroll && stage && stage.scrollWidth > stage.clientWidth + 1) {
      const delta = target.getBoundingClientRect().left - stage.getBoundingClientRect().left;
      stage.scrollLeft = stage.scrollLeft + delta;
    }
  }

  // Keep the thumbnail pressed-state honest when the shopper swipes the track directly.
  const schedule = (work) => (document.hidden || typeof requestAnimationFrame !== 'function')
    ? setTimeout(work, 0)
    : requestAnimationFrame(work);

  function watchGallery(product) {
    const stage = product.querySelector('.gallery-stage');
    if (!stage || stage.dataset.watched) return;
    stage.dataset.watched = 'true';
    let frame = 0;
    stage.addEventListener('scroll', () => {
      if (frame) return;
      frame = schedule(() => {
        frame = 0;
        if (stage.scrollWidth <= stage.clientWidth + 1) return;
        const box = stage.getBoundingClientRect();
        const middle = box.left + box.width / 2;
        const items = [...stage.querySelectorAll('[data-media-id]')];
        const current = items.find(item => { const rect = item.getBoundingClientRect(); return rect.left <= middle && rect.right > middle; });
        if (current) showMedia(product, current.dataset.mediaId, false);
      });
    }, { passive: true });
  }

  // Mark option values that cannot lead to a purchasable variant given the other current choices,
  // so a buyer stops brute-forcing combinations that dead-end.
  function markReachable(product) {
    const state = products.get(product);
    if (!state) return;
    if (state.data.variantCount > state.data.variants.length) return;
    const controls = optionControls(product);
    const current = controls.map(readOption);
    controls.forEach((control, index) => {
      control.querySelectorAll('input').forEach((input) => {
        const reachable = state.data.variants.some(variant =>
          variant.available &&
          variant.options[index] === input.value &&
          current.every((value, other) => other === index || !value || variant.options[other] === value));
        input.toggleAttribute('data-unreachable', !reachable && !input.hasAttribute('data-sold-out'));
        const chip = input.closest('.chip');
        if (chip) chip.classList.toggle('chip--unreachable', !reachable);
      });
    });
  }

  function chosenVariant(product) {
    const state = products.get(product);
    const controls = optionControls(product);
    if (!controls.length) return state.data.variants.find(v => String(v.id) === product.querySelector('[data-variant-id]').value);
    if (controls.some(control => !readOption(control))) return null;
    return state.data.variants.find(variant => controls.every((control, index) => variant.options[index] === readOption(control))) || null;
  }

  // Before every option is chosen, the price narrows to what the choices so far allow: the cheapest
  // matching variant that can be bought, with "From" only while the matches differ. Never a price no
  // matching variant has (a logo option that adds $20 shows that at once, not after the size).
  function partialPrice(product, state) {
    const picked = optionControls(product).map(readOption);
    if (!picked.some(Boolean) || state.data.variantCount > state.data.variants.length) return '';
    const matches = state.data.variants.filter(variant => picked.every((value, index) => !value || variant.options[index] === value));
    const pool = matches.some(variant => variant.available) ? matches.filter(variant => variant.available) : matches;
    if (!pool.length || pool.some(variant => typeof variant.price !== 'number')) return '';
    const cheapest = pool.reduce((low, variant) => (variant.price < low.price ? variant : low));
    if (new Set(pool.map(variant => variant.price)).size === 1) return cheapest.priceHtml;
    return cheapest.priceHtml.replace(/^(\s*<span class="price[^"]*">)/, `$1<span class="price-prefix">${text.from}</span> `);
  }

  function applyVariant(product, variant, updateUrl = false) {
    const state = products.get(product);
    state.variant = variant;
    const variantInput = product.querySelector('[data-variant-id]');
    if (variant && ![...variantInput.options].some(option => option.value === String(variant.id))) variantInput.add(new Option(variant.options.join(' / '), variant.id));
    variantInput.value = variant ? String(variant.id) : '';
    const allChosen = optionControls(product).every(readOption);
    const blocked = product.dataset.customBlocked === 'true';
    const unavailable = allChosen && !variant;
    const add = product.querySelector('[data-add-button]');
    const plan = product.querySelector('[data-selling-plan]');
    if (plan) {
      const previous = plan.value;
      const first = new Option(variant?.requiresPlan ? text.choosePlan : text.oneTime, '');
      plan.replaceChildren(first);
      (variant?.plans || []).forEach(item => plan.add(new Option(item.name, item.id)));
      plan.required = !!variant?.requiresPlan;
      plan.disabled = !variant;
      if ([...plan.options].some(option => option.value === previous)) plan.value = previous;
      product.querySelector('[data-plan-field]').hidden = !variant?.plans?.length;
    }
    // Never disable the CTA merely because a choice has not been made yet: a dead red button reads
    // as a broken page. Disable only for genuinely unbuyable states; submit then guides the choice.
    const nothingBuyable = state.data.available === false;
    add.disabled = blocked || state.pending || state.uncertain || unavailable || nothingBuyable || (!!variant && !variant.available);
    add.textContent = blocked ? product.dataset.customLabel : nothingBuyable ? product.dataset.soldLabel : !variant ? (unavailable ? product.dataset.unavailableLabel : product.dataset.chooseLabel) : !variant.available ? product.dataset.soldLabel : product.dataset.addLabel;
    const price = product.querySelector('[data-product-price]');
    price.innerHTML = (nothingBuyable || blocked) ? state.initialPrice : variant ? variant.priceHtml : (partialPrice(product, state) || state.initialPrice);
    // The brand gift card shows the amount picked (snippets/gift-card-art), else the range it started with.
    const giftAmount = product.querySelector('[data-gift-amount]');
    if (giftAmount) {
      let amounts = {};
      try { amounts = JSON.parse(giftAmount.dataset.amounts || '{}'); } catch { /* the range stays */ }
      giftAmount.textContent = (variant && amounts[variant.id]) || giftAmount.dataset.initial;
    }
    if (variant) {
      const quantity = product.querySelector('[name="quantity"]');
      quantity.min = variant.min || 1; quantity.step = variant.step || 1;
      if (variant.max != null) quantity.max = variant.max; else quantity.removeAttribute('max');
      if (!quantity.checkValidity()) quantity.value = variant.min || 1;
      showMedia(product, variant.mediaId);
    }
    markReachable(product);
    syncPlan(product);
    if (updateUrl && product.dataset.urlSync === 'true' && !window.Shopify?.designMode) {
      const url = new URL(location.href);
      url.searchParams.delete('option_values');
      if (variant) url.searchParams.set('variant', variant.id); else url.searchParams.delete('variant');
      history.replaceState({}, '', url);
    }
    const message = product.querySelector('[data-product-status]');
    if (!state.pending && !state.uncertain) message.textContent = unavailable ? text.unavailable : '';
    syncBuyBar(product);
  }

  // The bar only earns its space once the real add button has scrolled up past the top of the screen
  // (not at first paint, when the button is still below it), and it steps back down when the footer
  // arrives: it has nothing to offer there, and on phones it covered the signup field and lifted the
  // chat launcher over it. A bar that holds keyboard focus is not put away by the footer until focus
  // leaves it; if the main Add button itself comes into view, focus moves to that button.
  function setupBuyBar(product) {
    const bar = product.querySelector('[data-buy-bar]');
    const add = product.querySelector('[data-add-button]');
    if (!bar || !add) return;
    const root = document.documentElement;
    // Set once, never toggled (theme.css: html.has-buy-bar keeps focus clear of the bar and the launcher).
    root.classList.add('has-buy-bar');
    // The Inbox launcher rides above the bar while it is up (and not behind an open dialog). Written on the
    // launcher element only, and only when that changes: a root or body style that followed the bar made the
    // browser restyle the whole page on every slide in and out.
    const LIFT = 'calc(var(--buy-bar-h, calc(72px + env(safe-area-inset-bottom, 0px))) + 8px)';
    let liftedChat = null;
    const liftChat = (up) => {
      const chat = up && !root.classList.contains('dialog-open') ? document.querySelector('shopify-chat') : null;
      if (chat === liftedChat) return;
      liftedChat?.style.removeProperty('--shopify-chat-activator-offset');
      chat?.style.setProperty('--shopify-chat-activator-offset', LIFT);
      liftedChat = chat;
    };
    if (!('IntersectionObserver' in window)) { bar.hidden = false; liftChat(true); return; }
    // Stowed from the first paint, so it never flashes up and slides away.
    bar.classList.add('is-stowed');
    bar.hidden = false;
    const onScreen = new Map();
    let passed = false;
    const update = () => {
      const holding = bar.contains(document.activeElement);
      const addOn = !!onScreen.get(add);
      const footerOn = [...onScreen].some(([node, on]) => node !== add && on);
      const stowed = addOn || !passed || (footerOn && !holding);
      // A stowed bar leaves the tab order (theme.css), so focus never falls back to the page.
      if (stowed && holding) add.focus({ preventScroll: !addOn });
      bar.classList.toggle('is-stowed', stowed);
      liftChat(!stowed);
    };
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        onScreen.set(entry.target, entry.isIntersecting);
        // Passed means above the screen; a button still below it (first paint on a phone) is not.
        if (entry.target === add) passed = !entry.isIntersecting && (entry.boundingClientRect ? entry.boundingClientRect.bottom <= (entry.rootBounds?.top ?? 0) + 1 : true);
      }
      update();
    }, { rootMargin: '0px 0px -72px 0px' });
    bar.addEventListener('focusout', () => setTimeout(update, 0));
    // A dialog opening or closing (.dialog-open on <html>) drops the launcher back to its corner or lifts it again.
    new MutationObserver(() => liftChat(!bar.classList.contains('is-stowed'))).observe(root, { attributes: true, attributeFilter: ['class'] });
    // An instant jump (the rating link, a #hash, Home or End) carries the button past the screen edge
    // without it ever being on screen, so the observer reports nothing: read its position instead.
    let ticking = 0;
    const reread = () => { ticking = 0; if (!add.isConnected) return; passed = !onScreen.get(add) && add.getBoundingClientRect().bottom <= 0; update(); };
    const onScroll = () => { if (!ticking) ticking = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(reread) : setTimeout(reread, 16); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('hashchange', onScroll);
    observer.observe(add);
    // The bar's rendered height (phone or desktop layout, safe area included) drives the chat launcher's
    // lift and the page-end room in theme.css, and the recent-orders pop reads it directly.
    // A bar that is stowed under reduced motion (display:none) or removed by a section reload measures 0:
    // keep the last real height rather than publishing that.
    if ('ResizeObserver' in window) new ResizeObserver(() => { if (bar.isConnected && bar.offsetHeight > 0) document.documentElement.style.setProperty('--buy-bar-h', `${bar.offsetHeight}px`); }).observe(bar);
    const watchFooter = () => {
      for (const node of onScreen.keys()) if (!node.isConnected) { onScreen.delete(node); observer.unobserve(node); }
      const footer = document.querySelector('.site-footer');
      if (footer && !onScreen.has(footer)) { onScreen.set(footer, false); observer.observe(footer); }
    };
    watchFooter();
    // The theme editor replaces the footer section's element when it is edited.
    document.addEventListener('shopify:section:load', watchFooter);
  }

  function syncPlan(product) {
    const state = products.get(product);
    const variant = state?.variant;
    const plan = product.querySelector('[data-selling-plan]');
    const allocation = variant?.plans?.find(item => String(item.id) === plan?.value);
    const price = product.querySelector('[data-plan-price]');
    if (price) price.textContent = allocation?.price || '';
    const accelerated = product.querySelector('[data-accelerated]');
    if (accelerated) accelerated.hidden = !variant?.available || state.pending || state.uncertain || (variant.requiresPlan && !allocation);
    syncBuyBar(product);
  }

  function syncBuyBar(product) {
    const state = products.get(product);
    const add = product.querySelector('[data-add-button]');
    const button = product.querySelector('[data-buy-bar-button]');
    if (!button) return;
    button.disabled = add.disabled || state.pending || state.uncertain;
    button.textContent = state.pending ? addingText(product) : add.textContent.trim();
    button.setAttribute('aria-busy', String(state.pending));
    const allocation = state.variant?.plans?.find(item => String(item.id) === product.querySelector('[data-selling-plan]')?.value);
    const price = product.querySelector('[data-buy-bar-price]');
    if (allocation) price.textContent = allocation.price;
    else price.innerHTML = product.querySelector('[data-product-price]').innerHTML;
  }

  async function changeVariant(product) {
    const state = products.get(product);
    state.controller?.abort();
    const variant = chosenVariant(product);
    const options = optionControls(product);
    // Shopify may expose only the first 250 variants. Resolve larger products server-side.
    if (!variant && options.every(readOption) && state.data.variantCount > state.data.variants.length) {
      const ids = options.map(readOptionId);
      if (ids.some(id => !id)) { applyVariant(product, null, true); return; }
      const controller = new AbortController(); state.controller = controller;
      applyVariant(product, null, false);
      product.querySelector('[data-product-status]').textContent = text.checkingOptions;
      try {
        const url = new URL(product.dataset.productUrl, location.origin);
        url.searchParams.set('option_values', ids.join(',')); url.searchParams.set('section_id', product.dataset.sectionId);
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(text.optionsFailed);
        const documentResult = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (controller.signal.aborted) return;
        const data = JSON.parse(documentResult.querySelector('[data-product-state]').textContent);
        const candidate = data.selectedVariant;
        const selected = candidate && options.every((control, index) => readOption(control) === candidate.options[index]) ? candidate : null;
        if (selected && !state.data.variants.some(item => item.id === selected.id)) state.data.variants.push(selected);
        applyVariant(product, selected, true);
      } catch (error) {
        if (error.name !== 'AbortError') product.querySelector('[data-product-status]').textContent = text.optionsFailed;
      }
    } else applyVariant(product, variant, true);
  }

  // Metric companion for an imperial size range (the SBD dual-unit pattern). Pure arithmetic on the
  // option value already shown; nothing is claimed that the value does not already say.
  const inchRange = /^(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)\s*(?:inches|inch|in|")$/i;
  function enhanceSizeChips(product) {
    product.querySelectorAll('[data-chip-range]').forEach((node) => {
      if (node.nextElementSibling?.matches('.chip-metric')) return;
      const match = node.textContent.trim().match(inchRange);
      if (!match) return;
      const cm = (value) => Math.round(parseFloat(value) * 2.54);
      const metric = document.createElement('span');
      metric.className = 'chip-metric';
      metric.textContent = `${cm(match[1])}–${cm(match[2])} ${text.cm || 'cm'}`;
      node.after(metric);
    });
    // The masthead rating jumps to the reviews only when a reviews section exists on this page.
    product.querySelectorAll('[data-reviews-link]').forEach((link) => {
      if (document.getElementById('ProductReviews')) return;
      const plain = document.createElement('span');
      plain.className = link.className;
      plain.append(...link.childNodes);
      link.replaceWith(plain);
    });
  }

  function init(container = document) {
    const nodes = [...container.querySelectorAll('[data-product]')];
    if (container.matches?.('[data-product]')) nodes.push(container);
    for (const product of nodes) {
      if (products.has(product)) continue;
      const data = JSON.parse(product.querySelector('[data-product-state]').textContent);
      const state = { data, initialPrice: product.querySelector('[data-product-price]').innerHTML, pending: false, uncertain: false };
      products.set(product, state);
      const selected = data.selectedVariant || data.variants.find(item => item.id === data.selectedId);
      if (selected && !data.variants.some(item => item.id === selected.id)) data.variants.push(selected);
      if (selected) optionControls(product).forEach((control, index) => writeOption(control, selected.options[index]));
      else presetThickness(product, thicknessHint(product));
      // Chip fieldsets ship disabled so a no-JS submit posts only the fallback select.
      product.querySelectorAll('fieldset[data-option-index][disabled]').forEach(field => { field.disabled = false; });
      // The fallback select is required for the no-JS path. Left required once hidden, constraint
      // validation silently blocks EVERY native submit — the browser cannot focus a hidden control,
      // so a click on Add produced no submit event, no message and no error. JS now owns the id.
      const fallback = product.querySelector('[data-variant-fallback]');
      fallback.hidden = true;
      const fallbackSelect = fallback.querySelector('[data-variant-id]');
      if (fallbackSelect) fallbackSelect.required = false;
      // Same for the artwork file: required for the no-JS post, checked by checkArtwork once JS runs.
      product.querySelectorAll('[data-artwork-field]').forEach((field) => {
        const input = field.querySelector('[data-artwork-input]');
        input.required = false; input.setAttribute('aria-required', 'true');
        checkArtwork(field, false);
      });
      product.querySelectorAll('[data-recipient-form]').forEach(setupRecipient);
      setupBuyBar(product);
      watchGallery(product);
      enhanceSizeChips(product);
      applyVariant(product, selected || chosenVariant(product));
    }
  }

  const sectionNames = () => document.querySelector('[data-main-cart]') ? ['cart-panel', document.querySelector('[data-main-cart]').closest('.shopify-section').id.replace('shopify-section-', '')] : ['cart-panel'];
  function applyCartSections(sections) {
    if (!sections?.['cart-panel']) throw new Error(text.cartRefreshFailed);
    const parsed = new DOMParser().parseFromString(sections['cart-panel'], 'text/html');
    const content = parsed.querySelector('[data-cart-content]');
    if (!content) throw new Error(text.cartRefreshFailed);
    const main = document.querySelector('[data-main-cart]');
    let page;
    if (main) {
      const key = sectionNames()[1];
      page = new DOMParser().parseFromString(sections[key] || '', 'text/html').querySelector('[data-main-cart]');
      if (!page) throw new Error(text.cartRefreshFailed);
    }
    document.querySelector('[data-cart-drawer-content]').replaceChildren(content);
    document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = content.dataset.itemCount; });
    // The bag link's name carries the count too ("Your bag, 2 items"); the server words it, plural included.
    if (content.dataset.bagLabel) document.querySelectorAll('[data-cart-label]').forEach(node => node.setAttribute('aria-label', content.dataset.bagLabel));
    if (main) main.replaceChildren(...page.childNodes);
    cartUnreconciled = false;
    document.querySelectorAll('[data-quick-add][data-uncertain]').forEach(form => {
      delete form.dataset.uncertain;
      form.querySelector('button[name="add"]').disabled = false;
      form.querySelector('[data-quick-add-status]').textContent = text.cartUpdated;
    });
  }
  async function refreshCart() {
    const url = new URL(root.dataset.cartUrl, location.origin); url.searchParams.set('sections', sectionNames().join(','));
    // No Accept: application/json here. On the /cart route that header makes Shopify answer with
    // cart JSON instead of the Section Rendering payload, so every refresh failed and locked checkout.
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error(text.cartRefreshFailed);
    applyCartSections(await response.json());
  }
  // The checkout button is deliberately excluded. Disabling it made a tap during the ~200ms
  // quantity update vanish with no feedback — a dead tap at the last step of the funnel.
  // It stays live and is queued behind the in-flight request instead. The theme only ever sets its
  // disabled state (an unreconciled bag), never clears it: the options app holds the same button
  // with `disabled` (see Checkout hand-off), and a fresh render brings a fresh button.
  function lockCart(lock) {
    cartBusy = lock;
    document.querySelectorAll('[data-cart-form] input,[data-cart-form] button').forEach(control => {
      if (control.matches('[data-checkout]')) {
        control.setAttribute('aria-busy', String(lock));
        if (cartUnreconciled) { control.disabled = true; control.dataset.blocked = ''; }
        return;
      }
      control.disabled = lock || cartUnreconciled;
    });
    document.querySelectorAll('[data-remove-line]').forEach(link => link.setAttribute('aria-disabled', String(lock || cartUnreconciled)));
    // A checkout tap is an intent, not a recursive submit waiting on an unrelated promise.
    // Every cart operation releases this lock in finally, including adds and recovery reads.
    if (!lock && checkoutIntent) queueMicrotask(settleCheckout);
  }
  // ---- Checkout hand-off -------------------------------------------------------------------
  // Eazy Product Options (app embed, mepc-script.js) re-reads the cart after every request to a
  // /cart/ URL, sets `disabled` on every [name=checkout] while it matches its option-charge lines to
  // their items, and clears it 500 ms after it finishes: about 0.65 s after each bag change, and
  // 1–5 s after a /cart page load. A tap on a disabled button produces no event at all, so a shopper
  // who changed the bag and went straight to checkout lost the tap without a word. The theme never
  // writes that attribute. While it is set, the button's wrapper takes the tap (theme.css), and the
  // tap waits, with a visible message, until the theme's update and the app's check have both
  // finished; then the form is submitted once.
  const CHECKOUT_APP_GRACE = 1500;
  const CHECKOUT_WAIT_LIMIT = 15000;
  let checkoutLeaving = false;
  let checkoutTimer = 0;
  let lastCartChange = -Infinity;
  let appCheckedAt = -Infinity;
  // The app announces each check it starts; a bag carrying its charge lines waits for that check.
  window.addEventListener('mepc-cart-change', () => { appCheckedAt = performance.now(); });
  function checkoutState(form) {
    const button = form?.querySelector('[data-checkout]');
    if (!button) return 'gone';
    if (cartUnreconciled) return 'blocked';
    if (cartBusy || button.disabled || shelfQueue.size) return 'wait';
    // Charge lines are repriced by the app after a change; give it up to CHECKOUT_APP_GRACE to start
    // checking the latest bag, so the charges reach checkout matched to their items.
    if (form.hasAttribute('data-app-charges') && appCheckedAt < lastCartChange && performance.now() - lastCartChange < CHECKOUT_APP_GRACE) return 'wait';
    return 'ready';
  }
  function markCheckoutPending(on) {
    document.querySelectorAll('[data-checkout-hold]').forEach(hold => hold.classList.toggle('is-pending', on));
    if (!on) return;
    // A re-rendered bag brings an empty status line; say it again there, without re-announcing it.
    document.querySelectorAll('[data-cart-status]').forEach(node => { if (node.textContent !== text.checkoutPending) node.textContent = text.checkoutPending; });
  }
  // After it re-prices charge lines the app reloads the page (its drawer refresh for themes it does
  // not recognise). A tap waiting on that check is remembered for the same window and carried on
  // after the reload, against the bag the app has just settled (see the resume after pageshow).
  const INTENT_KEY = 'akinci:checkout-intent';
  const rememberIntent = (at) => { try { if (at) sessionStorage.setItem(INTENT_KEY, String(at)); else sessionStorage.removeItem(INTENT_KEY); } catch { /* storage unavailable */ } };
  function cancelCheckout() {
    checkoutIntent = null;
    clearTimeout(checkoutTimer);
    markCheckoutPending(false);
    rememberIntent(0);
  }
  function requestCheckout(form) {
    if (checkoutLeaving) return;
    if (!checkoutIntent) {
      checkoutIntent = { formId: form.id, since: performance.now() };
      cartMessage(text.checkoutPending);
      if (form.hasAttribute('data-app-charges')) rememberIntent(Date.now());
    }
    settleCheckout();
  }
  function settleCheckout() {
    clearTimeout(checkoutTimer);
    const intent = checkoutIntent;
    if (!intent) return;
    const form = document.getElementById(intent.formId) || document.querySelector('[data-cart-form]');
    const state = checkoutState(form);
    if (state === 'ready') { cancelCheckout(); form.requestSubmit(form.querySelector('[data-checkout]')); return; }
    if (state === 'gone') { cancelCheckout(); return; }
    if (state === 'blocked') { cancelCheckout(); cartMessage(text.cartRefreshFailed); return; }
    // Never force the button: if the hold outlasts any normal check, say so and let the shopper retry.
    if (performance.now() - intent.since > CHECKOUT_WAIT_LIMIT) { cancelCheckout(); cartMessage(text.checkoutStalled); return; }
    markCheckoutPending(true);
    checkoutTimer = setTimeout(settleCheckout, 100);
  }
  // Every cart outcome must land somewhere the shopper can see, not only in the visually-hidden
  // live region. cartMessage writes both.
  function cartMessage(message) {
    document.querySelectorAll('[data-cart-status]').forEach(node => { node.textContent = message; });
    status(message);
  }
  function cartRecovery() {
    cartUnreconciled = true;
    const drawer = document.querySelector('[data-cart-drawer-content]');
    if (drawer && !drawer.querySelector('[data-cart-content]')) {
      const shell = document.createElement('div');
      shell.className = 'cart-content';
      shell.dataset.cartContent = '';
      drawer.replaceChildren(shell);
    }
    document.querySelectorAll('[data-cart-content]').forEach(content => {
      let notice = content.querySelector('[data-cart-recovery]');
      if (!notice) { notice = document.createElement('p'); notice.dataset.cartRecovery = ''; notice.className = 'notice'; content.prepend(notice); }
      notice.textContent = text.cartRefreshFailed + ' ';
      const retry = document.createElement('button');
      retry.type = 'button'; retry.className = 'button button-small'; retry.dataset.retryCart = '';
      retry.textContent = text.retry;
      const link = document.createElement('a'); link.href = root.dataset.cartUrl; link.textContent = text.viewCart;
      notice.append(retry, ' ', link);
    });
  }
  function addRecovery(product, messageText, needsReload = false) {
    const message = product.querySelector('[data-product-status]');
    message.textContent = messageText;
    const cartLink = document.createElement('a'); cartLink.href = root.dataset.cartUrl; cartLink.textContent = text.viewCart;
    message.append(' ', cartLink);
    if (needsReload) {
      const reload = document.createElement('a'); reload.href = location.href; reload.textContent = text.reloadProduct; reload.dataset.reloadProduct = '';
      message.append(' ', reload);
    }
    revealStatus(message);
  }
  // A tap on the sticky bar leaves the status line (under the main Add) above the screen; bring the answer
  // to the shopper. "Off screen" by its box was not enough: while the line above the screen grows with the
  // new text, Chromium's scroll anchoring moves the page with it, and the line ended a pixel inside the top
  // edge, unread (8 Oct, 390 and 430). So the whole line must sit in the clear part of the screen, below the
  // header while that is on screen and above the shown buy bar, measured in the next frame, once the text,
  // its links and any anchoring have been laid out. Inside a dialog the dialog's box is the clear part.
  function revealStatus(message) {
    schedule(() => {
      if (!message.isConnected) return;
      const box = message.getBoundingClientRect();
      if (!box.width && !box.height) return;
      let top = 0;
      let bottom = window.innerHeight;
      const dialog = message.closest('dialog');
      if (dialog) {
        const frame = dialog.getBoundingClientRect();
        top = Math.max(top, frame.top); bottom = Math.min(bottom, frame.bottom);
      } else {
        const header = document.querySelector('.site-header')?.getBoundingClientRect();
        if (header && header.bottom > top && header.top < bottom) top = header.bottom;
        const bar = document.querySelector('.buy-bar[data-buy-bar]:not([hidden]):not(.is-stowed)');
        if (bar?.offsetHeight) bottom = Math.min(bottom, bar.getBoundingClientRect().top);
      }
      if (box.top >= top && box.bottom <= bottom) return;
      const calm = true; // Halloween theme: no smooth scrolling for anyone
      try { message.scrollIntoView({ block: 'center', behavior: calm ? 'auto' : 'smooth' }); } catch { /* non-visual host */ }
    });
  }
  // The product page's add disables the button that started it (the main Add or the sticky bar's) while
  // the request runs, and a disabled button loses focus to the page: after a refused add a keyboard or
  // screen-reader user was left on <body>, far from the reason and the button. When the add ends with
  // focus still nowhere (no drawer took it), it goes back to the button that was used if that works again
  // (a refusal: its reason is already in the status line, a live region), else to the main Add, else to
  // the status line itself (an add that could not be confirmed keeps both buttons disabled until the bag
  // is checked). Focus the shopper moved elsewhere meanwhile is left where it is.
  function addFocusOrigin(product) {
    const active = document.activeElement;
    return active && (active === product.querySelector('[data-add-button]') || active === product.querySelector('[data-buy-bar-button]')) ? active : null;
  }
  function settleAddFocus(origin, product) {
    if (!origin) return;
    const active = document.activeElement;
    if (active && active !== document.body) return;
    for (const target of [origin, product.querySelector('[data-add-button]')]) {
      if (!target?.isConnected || target.disabled) continue;
      target.focus({ preventScroll: true });
      // A bar that has stepped away is not focusable: try the main Add.
      if (document.activeElement === target) return;
    }
    const message = product.querySelector('[data-product-status]');
    if (!message) return;
    message.tabIndex = -1;
    message.focus({ preventScroll: true });
  }
  // ---- Custom belt artwork ------------------------------------------------------------------
  // The custom belt's artwork is a required file sent with the add (snippets/artwork-field). Left to the
  // browser, its required check stops a click on Add before the submit handler runs: no reason beside
  // the field, and a missing option never named first. So once JS runs it owns presence, type and size,
  // and says what is wrong under the field itself (aria-invalid, described by the error line).
  const artworkSize = (bytes) => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
  function setArtworkError(field, problem) {
    const input = field.querySelector('[data-artwork-input]');
    field.querySelector('[data-artwork-error]').textContent = problem;
    if (problem) input.setAttribute('aria-invalid', 'true'); else input.removeAttribute('aria-invalid');
  }
  // Reads the chosen file and returns whether it can be sent. A file that cannot is let go at once, so it
  // never reaches the cart; its name stays in the message. While submitting, no file is a problem too.
  function checkArtwork(field, submitting) {
    const input = field.querySelector('[data-artwork-input]');
    const original = field.querySelector('[data-artwork-original]');
    const chosen = field.querySelector('[data-artwork-name]');
    const file = input.files?.[0] || null;
    const list = (value) => (value || '').split(/\s+/).filter(Boolean);
    const extension = file && file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : '';
    // By extension; a name without one (some phone pickers) by the type the browser reports.
    const accepted = file && (list(field.dataset.artworkTypes).includes(extension) || list(field.dataset.artworkMime).includes(file.type));
    let problem = '';
    if (!file) problem = submitting ? field.dataset.errorMissing : '';
    else if (!accepted) problem = field.dataset.errorType;
    else if (file.size > Number(field.dataset.artworkMax)) problem = field.dataset.errorSize;
    else if (file.size === 0) problem = field.dataset.errorEmpty;
    if (file && problem) { input.value = ''; problem = `${file.name}: ${problem}`; }
    const kept = problem ? null : file;
    chosen.textContent = kept ? `${field.dataset.selectedLabel} ${kept.name} (${artworkSize(kept.size)})` : '';
    if (original) { original.value = kept ? kept.name : ''; original.disabled = !kept; }
    setArtworkError(field, problem);
    return !!kept;
  }
  // While an add runs, the status line and the sticky bar's button say the same thing; with a file it is
  // the upload.
  function addingText(product) {
    return product.querySelector('[data-artwork-field]') ? (text.uploading || text.adding) : text.adding;
  }
  // A link to a file Shopify stored: no whitespace, https or protocol-relative, and either on Shopify's
  // file CDN under the shop's uploads/ or files/ (cdn.shopify.com/s/files/1/…/uploads/…), or on the store's
  // own address under /cdn/shop/uploads/ or /cdn/shop/files/. A theme asset or product photo is not one.
  function storedFile(value, shopUrl) {
    if (typeof value !== 'string' || /\s/.test(value) || !/^(?:https:)?\/\//.test(value)) return false;
    let url;
    try { url = new URL(value, 'https://cdn.shopify.com'); } catch { return false; }
    if (url.protocol !== 'https:') return false;
    if (url.host === 'cdn.shopify.com') return /^\/s\/files\/(?:[^/]+\/)*?(?:uploads|files)\/[^/]/.test(url.pathname);
    const own = new Set([location.host]);
    try { if (shopUrl) own.add(new URL(shopUrl).host); } catch { /* no store address */ }
    return own.has(url.host) && /^\/cdn\/shop\/(?:uploads|files)\/[^/]/.test(url.pathname);
  }
  // A 2xx from /cart/add.js only says a line was added. With the artwork field, the line Shopify answers
  // with must also carry the stored file: its artwork property (the file input's own name) is the link to
  // it. Nothing, or the file's name, means the file never arrived.
  function uploadedArtwork(field, result, variantId) {
    const key = field.querySelector('[data-artwork-input]').name.match(/^properties\[(.+)\]$/)?.[1];
    const line = Array.isArray(result?.items) ? result.items.find(item => String(item.variant_id ?? item.id) === String(variantId)) : result;
    return !!key && storedFile(line?.properties?.[key], field.dataset.shopUrl);
  }
  // ---- Gift card recipient ------------------------------------------------------------------
  // Shopify's recipient form (snippets/gift-card-recipient-form). Ticked, the checkbox property
  // __shopify_send_gift_card_to_recipient goes with the add and the recipient email is required; unticked,
  // the fields are disabled, so nothing of them is sent and Shopify emails the card to the buyer. The
  // no-JS "if_present" control is removed here. Values survive an untick (a slip of the finger costs
  // nothing) and are cleared after a confirmed add, so the next card does not go to the same person.
  const RECIPIENT_PROPERTIES = ['Recipient email', 'Recipient name', 'Message', 'Send on'];
  const SEND_WINDOW_DAYS = 90;
  const isoDay = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const recipientInput = (box, key) => box.querySelector(`[data-recipient-input="${key}"]`);
  function setupRecipient(box) {
    const toggle = box.querySelector('[data-recipient-toggle]');
    if (!toggle || box.dataset.ready) return;
    box.dataset.ready = 'true';
    toggle.disabled = false;
    const control = box.querySelector('[data-recipient-control]');
    if (control) control.disabled = true;
    // Send on is the shopper's own calendar day: today up to 90 days ahead (Shopify's limit), and the
    // timezone offset Shopify reads to send it on that day where the shopper is.
    const date = recipientInput(box, 'send_on');
    if (date) { const today = new Date(); const last = new Date(today); last.setDate(today.getDate() + SEND_WINDOW_DAYS); date.min = isoDay(today); date.max = isoDay(last); }
    const offset = box.querySelector('[data-recipient-offset]');
    if (offset) offset.value = String(new Date().getTimezoneOffset());
    // The theme checks the recipient itself (checkRecipient) and says why under the field. Left to the
    // browser, an empty or mistyped email, or a date out of range, stopped the click on Add before the
    // submit handler ran, with only the browser's own bubble. The handler still asks the form to report
    // on its other fields (form.reportValidity). An options app that owns the form keeps the browser's.
    const form = box.closest('form');
    if (form && box.closest('[data-product]')?.dataset.nativeForm !== 'true') form.noValidate = true;
    syncRecipient(box);
    countMessage(box);
  }
  function syncRecipient(box) {
    const on = box.querySelector('[data-recipient-toggle]').checked;
    box.classList.toggle('is-open', on);
    box.querySelectorAll('[data-recipient-input],[data-recipient-offset]').forEach((input) => { input.disabled = !on; });
    const email = recipientInput(box, 'email');
    if (email) email.required = on;
    if (!on) clearRecipientErrors(box);
  }
  function countMessage(box) {
    const message = recipientInput(box, 'message');
    const count = box.querySelector('[data-recipient-count]');
    if (message && count) count.textContent = `${message.value.length}/${count.dataset.max || message.maxLength}`;
  }
  function setRecipientError(box, key, problem) {
    const input = recipientInput(box, key);
    const slot = box.querySelector(`[data-recipient-error="${key}"]`);
    if (slot) slot.textContent = problem;
    if (input) { if (problem) input.setAttribute('aria-invalid', 'true'); else input.removeAttribute('aria-invalid'); }
  }
  function clearRecipientErrors(box) {
    box.querySelectorAll('[data-recipient-error]').forEach(slot => setRecipientError(box, slot.dataset.recipientError, ''));
  }
  function focusRecipient(input) {
    input.focus({ preventScroll: true });
    const calm = true; // Halloween theme: no smooth scrolling for anyone
    try { input.scrollIntoView({ block: 'center', behavior: calm ? 'auto' : 'smooth' }); } catch { /* non-visual host */ }
  }
  // The checks Shopify makes, made before the add, so the reason sits under the field instead of coming
  // back as a refused add: an email when the card goes to someone, and a send date inside the window.
  function checkRecipient(box) {
    clearRecipientErrors(box);
    if (!box.querySelector('[data-recipient-toggle]')?.checked) return true;
    let first = null;
    const email = recipientInput(box, 'email');
    if (email) {
      email.value = email.value.trim();
      const problem = !email.value ? box.dataset.errorEmailMissing : (email.validity && !email.validity.valid) ? box.dataset.errorEmailInvalid : '';
      if (problem) { setRecipientError(box, 'email', problem); first = email; }
    }
    const date = recipientInput(box, 'send_on');
    // A half-typed date reads as empty (badInput): sent as is, the card would go out at once, unasked.
    if (date && (date.validity?.badInput || (date.value && (!/^\d{4}-\d{2}-\d{2}$/.test(date.value) || (date.min && date.value < date.min) || (date.max && date.value > date.max))))) {
      setRecipientError(box, 'send_on', box.dataset.errorDate); first = first || date;
    }
    if (first) { focusRecipient(first); return false; }
    return true;
  }
  // A refused add names the fields it refused (errors: { email: [...], send_on: [...] }): each reason goes
  // under its field. Returns whether any did.
  function showRecipientErrors(box, errors) {
    if (!errors || typeof errors !== 'object' || Array.isArray(errors)) return false;
    const keys = Object.keys(errors).filter(key => recipientInput(box, key));
    keys.forEach(key => setRecipientError(box, key, [].concat(errors[key]).join(', ')));
    if (keys.length) focusRecipient(recipientInput(box, keys[0]));
    return keys.length > 0;
  }
  function resetRecipient(box) {
    const toggle = box.querySelector('[data-recipient-toggle]');
    if (!toggle) return;
    toggle.checked = false;
    box.querySelectorAll('[data-recipient-input]').forEach((input) => { input.value = ''; });
    syncRecipient(box);
    countMessage(box);
  }
  // Only the recipient's details travel with a gift card. Apps that write into every product form (the
  // delivery-date app adds "Estimated between", twice) would put a delivery estimate on an emailed card's
  // order line. Private properties (a leading underscore, Shopify's own recipient flag and offset among
  // them) stay; empty optional fields are left off rather than sent blank.
  function giftCardProperties(data) {
    for (const key of new Set(data.keys())) {
      const name = key.match(/^properties\[(.*)\]$/)?.[1];
      if (name === undefined || name.startsWith('_')) continue;
      if (!RECIPIENT_PROPERTIES.includes(name) || !String(data.get(key)).trim()) data.delete(key);
    }
  }
  function quickRecovery(form, messageText) {
    form.dataset.uncertain = 'true';
    const note = form.querySelector('[data-quick-add-status]');
    note.textContent = messageText;
    const link = document.createElement('a'); link.href = root.dataset.cartUrl; link.textContent = text.viewCart;
    note.append(' ', link);
    cartRecovery();
  }
  function updateLine(line, quantity) {
    if (cartBusy || cartUnreconciled) return pendingCart;
    return trackCart(runUpdateLine(line, quantity));
  }
  const CART_LINE_CONTROLS = 'a[href],button,input,select,textarea';
  // After an extra is added from the bag's suggestions the suggestion that held focus is gone (the
  // product is in the bag now), so focus moves to its new line in the same bag and the bag says so.
  // Which bag a suggestion sits in ('drawer' | 'page'), or null for a control outside the bag.
  const shelfOf = (node) => node.closest('[data-cart-recs]') ? (node.closest('#CartDrawer') ? 'drawer' : 'page') : null;
  const shelfArea = (where) => where === 'page' ? document.querySelector('[data-main-cart]') : document.getElementById('CartDrawer');
  const lineIn = (where, variantId) => variantId ? shelfArea(where)?.querySelector(`[data-cart-line][data-variant-id="${CSS.escape(String(variantId))}"]`) : null;
  const focusTarget = (target) => {
    if (target && !target.matches('a,button,input,select')) target.tabIndex = -1;
    target?.focus();
  };
  function focusAddedLine(where, variantId) {
    focusTarget(lineIn(where, variantId)?.querySelector('.cart-item-details a') || shelfArea(where)?.querySelector('[data-cart-status]'));
    cartMessage(text.added);
  }
  // An add from the suggestions that did not land: the bag is redrawn, so the button that held focus is
  // gone. Focus returns to the same suggestion when it is still offered (its note says why, visibly),
  // else to the bag's status line, and the bag says why.
  function focusShelf(where, productId, message) {
    const rec = productId ? shelfArea(where)?.querySelector(`[data-cart-rec][data-product-id="${CSS.escape(String(productId))}"]`) : null;
    const note = rec?.querySelector('[data-quick-add-status]');
    if (note) note.textContent = message;
    focusTarget(rec?.querySelector('button:not([disabled])') || shelfArea(where)?.querySelector('[data-cart-status]'));
    cartMessage(message);
  }
  // The bag could not be read back: its recovery notice (Retry, View cart) is where the shopper goes next.
  const focusRetry = (where) => focusTarget(shelfArea(where)?.querySelector('[data-retry-cart]'));
  // A tap on a suggestion's Add while the bag is busy (a quantity field's change fires on the same press,
  // as focus leaves it, or another suggestion is being added). Every tapped suggestion is held once, said
  // on its button, and sent in turn once the bag is idle, from its redrawn form; checkout waits for them.
  // Gone by then (no longer suggested): nothing is sent. Still busy after the checkout wait limit, however
  // long a request hangs: the bag says the add did not go through.
  let shelfDraining = false;
  const shelfForm = (where, productId) => shelfArea(where)?.querySelector(`[data-cart-rec][data-product-id="${CSS.escape(String(productId))}"] form[data-quick-add]`);
  const markQueued = (form, on) => {
    const button = form?.querySelector('button[name="add"]'); const note = form?.querySelector('[data-quick-add-status]');
    if (on) button?.setAttribute('aria-busy', 'true'); else button?.removeAttribute('aria-busy');
    if (note) note.textContent = on ? text.adding : '';
  };
  function queueShelfAdd(form, where, productId) {
    if (!productId || shelfQueue.has(productId)) return;
    shelfQueue.set(productId, where);
    markQueued(form, true);
    if (!shelfDraining) drainShelf();
  }
  async function drainShelf() {
    shelfDraining = true;
    try {
      while (shelfQueue.size) {
        const since = performance.now();
        await Promise.race([pendingCart, new Promise(resolve => setTimeout(resolve, CHECKOUT_WAIT_LIMIT))]);
        while (cartBusy && performance.now() - since < CHECKOUT_WAIT_LIMIT) await new Promise(resolve => setTimeout(resolve, 100));
        // A redraw brings fresh suggestion forms: say again which ones are still waiting.
        for (const [id, area] of shelfQueue) markQueued(shelfForm(area, id), true);
        const [productId, where] = shelfQueue.entries().next().value;
        if (cartBusy || cartUnreconciled) {
          for (const [id, area] of shelfQueue) markQueued(shelfForm(area, id), false);
          shelfQueue.clear();
          if (cartUnreconciled) { cartMessage(text.cartRefreshFailed); focusRetry(where); }
          else focusShelf(where, productId, text.addFailed);
          break;
        }
        const current = shelfForm(where, productId);
        markQueued(current, false);
        shelfQueue.delete(productId);
        // The add takes the bag's lock in the same task, so checkout keeps waiting without a gap.
        current?.requestSubmit(current.querySelector('button[name="add"]'));
      }
    } finally { shelfDraining = false; }
  }
  // Where keyboard focus sits in the bag, described so it survives the re-render: the line (by key) and
  // the control on it. A quantity change fires as focus LEAVES the field, so by the time the bag is
  // redrawn the shopper may already be on the line's Remove link or product link (Tab, Shift+Tab).
  function cartFocusMark() {
    const active = document.activeElement;
    const area = active?.closest?.('#CartDrawer, [data-main-cart]');
    const line = active?.closest?.('[data-cart-line]');
    if (!area || !line) return null;
    const role = active.matches('[data-line-quantity]') ? '[data-line-quantity]' : active.matches('[data-remove-line]') ? '[data-remove-line]' : null;
    return { drawer: area.id === 'CartDrawer', key: line.dataset.key, role, index: [...line.querySelectorAll(CART_LINE_CONTROLS)].indexOf(active) };
  }
  // Put focus back on the same control of the same line after a redraw. False when the line is gone.
  function restoreCartFocus(mark) {
    if (!mark || (document.activeElement && document.activeElement !== document.body)) return false;
    const area = mark.drawer ? document.getElementById('CartDrawer') : document.querySelector('[data-main-cart]');
    const line = area && [...area.querySelectorAll('[data-cart-line]')].find(item => item.dataset.key === mark.key);
    if (!line) return false;
    const target = (mark.role && line.querySelector(mark.role)) || line.querySelectorAll(CART_LINE_CONTROLS)[mark.index] || line.querySelector('[data-line-quantity]');
    target?.focus();
    return !!target;
  }
  async function runUpdateLine(line, quantity) {
    const active = document.activeElement;
    const focusId = active?.id;
    const wasRemove = active?.matches('[data-remove-line]');
    let focusMark = null;
    lockCart(true);
    let message = '';
    let changed = false;
    try {
      const response = await fetch(`${root.dataset.changeUrl}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: line.dataset.key, quantity, sections: sectionNames(), sections_url: root.dataset.cartUrl }) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || text.cartUpdateFailed);
      focusMark = cartFocusMark();
      try { applyCartSections(result.sections); } catch { await refreshCart(); }
      changed = true;
      lastCartChange = performance.now();
      message = text.cartUpdated;
    } catch (error) {
      message = error.message === 'Failed to fetch' ? text.cartUpdateFailed : error.message;
      // Never replay a mutation after a transport failure. Reconcile with a read.
      focusMark = focusMark || cartFocusMark();
      try { await refreshCart(); } catch { message = text.cartRefreshFailed; cartRecovery(); }
    } finally {
      // A change that did not land cancels a waiting checkout; the reason shows instead.
      if (!changed) cancelCheckout();
      lockCart(false);
      cartMessage(checkoutIntent ? text.checkoutPending : message);
      if (restoreCartFocus(focusMark)) { /* focus is back on the control the shopper moved to */ }
      else if (focusId) document.getElementById(focusId)?.focus();
      else if (wasRemove) (document.querySelector('dialog[open] [data-close-dialog]') || document.querySelector('[data-main-cart] a'))?.focus();
    }
  }

  document.addEventListener('click', async (event) => {
    // A tap on checkout while the options app holds it lands on the wrapper (theme.css). Keep it.
    if (event.target.matches?.('[data-checkout-hold]')) {
      const button = event.target.querySelector('[data-checkout]');
      if (button?.disabled && button.form) { event.preventDefault(); requestCheckout(button.form); }
      return;
    }
    const target = event.target.closest('a,button,dialog'); if (!target) return;
    if (target.matches('[data-reload-product]')) { event.preventDefault(); location.reload(); return; }
    if (target.matches('[data-open-zoom]')) {
      const dialog = document.getElementById(target.dataset.openZoom);
      if (dialog) {
        event.preventDefault();
        const stage = dialog.querySelector('[data-zoom-stage]');
        const image = document.createElement('img');
        image.src = target.href; image.alt = target.querySelector('img')?.alt || '';
        image.width = target.querySelector('img')?.width || 1200;
        image.height = target.querySelector('img')?.height || 1200;
        stage.replaceChildren(image); stage.classList.remove('is-zoomed');
        const toggle = dialog.querySelector('[data-zoom-toggle]');
        toggle.setAttribute('aria-pressed', 'false'); toggle.textContent = toggle.dataset.zoomIn;
        openDialog(dialog, target);
      }
    }
    if (target.matches('[data-zoom-toggle]')) {
      const stage = target.closest('dialog').querySelector('[data-zoom-stage]');
      const expanded = stage.classList.toggle('is-zoomed');
      target.setAttribute('aria-pressed', String(expanded));
      target.textContent = expanded ? target.dataset.zoomOut : target.dataset.zoomIn;
      if (expanded) stage.focus();
    }
    if (cartBusy && target.matches('a') && target.closest('[data-cart-content]')) { event.preventDefault(); return; }
    if (target.matches('[data-quick-select]')) { event.preventDefault(); openQuickSelect(target); return; }
    if (target.matches('[data-open-dialog]')) { const dialog = document.getElementById(target.dataset.openDialog); if (dialog) { event.preventDefault(); openDialog(dialog, target); } }
    if (target.matches('[data-close-dialog]')) closeDialog(target.closest('dialog'));
    if (target instanceof HTMLDialogElement && event.target === target) {
      const rect = target.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDialog(target);
    }
    if (target.matches('[data-show-media]')) showMedia(target.closest('[data-product]'), target.dataset.showMedia);
    if (target.matches('[data-open-cart]')) {
      event.preventDefault(); openDialog(document.getElementById('CartDrawer'), target);
      if (!cartBusy) { lockCart(true); try { await trackCart(refreshCart()); } catch { cartRecovery(); cartMessage(text.cartRefreshFailed); } finally { lockCart(false); } }
    }
    if (target.matches('[data-retry-cart]')) {
      event.preventDefault();
      if (cartBusy) return;
      cartUnreconciled = false; lockCart(true); cartMessage('');
      try { await refreshCart(); cartMessage(text.cartRefreshed); }
      catch { cartRecovery(); cartMessage(text.cartRefreshFailed); }
      finally { lockCart(false); }
      return;
    }
    if (target.matches('[data-remove-line]')) { event.preventDefault(); if (!cartBusy && !cartUnreconciled) await updateLine(target.closest('[data-cart-line]'), 0); }
  });

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (target.closest('[data-option-index]')) changeVariant(target.closest('[data-product]'));
    if (target.matches('[data-selling-plan]')) syncPlan(target.closest('[data-product]'));
    if (target.matches('[data-recipient-toggle]')) syncRecipient(target.closest('[data-recipient-form]'));
    if (target.matches('[data-artwork-input]')) {
      // Focus is back on the field once the file picker closes. A refused file is also said aloud; an
      // accepted one (or none) clears whatever was said about the last.
      const field = target.closest('[data-artwork-field]');
      announce(checkArtwork(field, false) ? '' : field.querySelector('[data-artwork-error]').textContent);
    }
    if (target.matches('[data-line-quantity]') && target.reportValidity()) updateLine(target.closest('[data-cart-line]'), Number(target.value));
  });
  // Recipient fields: the message keeps its count, and a reason under a field goes once it is edited.
  document.addEventListener('input', (event) => {
    const input = event.target;
    const box = input.closest?.('[data-recipient-form]');
    if (!box || !input.dataset.recipientInput) return;
    if (input.dataset.recipientInput === 'message') countMessage(box);
    if (input.getAttribute('aria-invalid') === 'true') setRecipientError(box, input.dataset.recipientInput, '');
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (event.defaultPrevented) return;
    if (form.matches('[data-cart-form]')) {
      if (cartUnreconciled) { event.preventDefault(); return; }
      if (event.submitter?.name !== 'checkout') { if (cartBusy) event.preventDefault(); return; }
      // One navigation per decision: a second tap while the first is leaving is dropped.
      if (checkoutLeaving) { event.preventDefault(); return; }
      if (checkoutState(form) === 'ready') {
        cancelCheckout();
        checkoutLeaving = true;
        setTimeout(() => { checkoutLeaving = false; }, 5000); // in case something else stops the navigation
        return;
      }
      // Section rendering replaces the form, so keep only its stable identity. Repeated taps
      // coalesce into one checkout once the bag is ready.
      event.preventDefault();
      requestCheckout(form);
      return;
    }
    if (form.matches('[data-quick-add]')) {
      // A card form has no variant state to manage: one fixed id, quantity one. It reuses the same
      // transport, the same never-replay rule and the same drawer as the product page.
      event.preventDefault();
      // An extra from the bag's own suggestions: the bag is already in view, so nothing else opens, and
      // every outcome lands back in that bag (focus and message) rather than in another panel.
      const shelf = shelfOf(form);
      const recId = form.closest('[data-cart-rec]')?.dataset.productId;
      const variantId = form.querySelector('[name="id"]')?.value;
      if (cartUnreconciled) { if (shelf) cartMessage(text.cartRefreshFailed); return; }
      // Already held for the bag: the same intent, sent once by the queue.
      if (shelf && recId && shelfQueue.has(recId)) return;
      if (cartBusy) { if (shelf) queueShelfAdd(form, shelf, recId); return; }
      const button = form.querySelector('button[name="add"]');
      const note = form.querySelector('[data-quick-add-status]');
      button.disabled = true; button.setAttribute('aria-busy', 'true');
      note.textContent = text.adding;
      lockCart(true);
      let confirmed = false;
      try {
        const data = new FormData(form);
        data.set('sections', sectionNames().join(',')); data.set('sections_url', root.dataset.cartUrl);
        const response = await fetch(`${root.dataset.addUrl}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: data });
        const result = await response.json();
        if (!response.ok) {
          note.textContent = result.description || result.message || text.addFailed;
          const reason = note.textContent;
          try { await refreshCart(); if (shelf) focusShelf(shelf, recId, reason); }
          catch { quickRecovery(form, reason + ' ' + text.cartRefreshFailed); if (shelf) focusRetry(shelf); }
          return;
        }
        confirmed = true; lastCartChange = performance.now();
        try { applyCartSections(result.sections); } catch { await refreshCart(); }
        note.textContent = '';
        status(text.added);
        if (shelf) focusAddedLine(shelf, variantId);
        else openDialog(document.getElementById('CartDrawer'), button);
      } catch {
        try {
          await refreshCart();
          note.textContent = confirmed ? '' : text.addUncertain;
          status(confirmed ? text.added : text.addUncertain);
          // In the bag, the redrawn bag itself settles an unconfirmed add: its line is there or it is not.
          if (shelf) {
            if (confirmed || lineIn(shelf, variantId)) focusAddedLine(shelf, variantId);
            else focusShelf(shelf, recId, text.addUncertain);
          } else {
            openDialog(document.getElementById('CartDrawer'), button);
            if (!confirmed) cartMessage(text.addUncertain);
          }
        } catch {
          quickRecovery(form, confirmed ? text.addedRefreshFailed : text.addUncertain);
          if (shelf) focusRetry(shelf);
        }
      } finally {
        if (!confirmed) cancelCheckout();
        lockCart(false); button.removeAttribute('aria-busy'); button.disabled = cartUnreconciled;
      }
      return;
    }
    if (!form.matches('.product-form')) return;
    const product = form.closest('[data-product]'); const state = products.get(product);
    if (product.dataset.customBlocked === 'true') { event.preventDefault(); return; }
    // An options app that owns the form (the native_form setting) submits it natively, upload and
    // validation included. The custom belt's own artwork field goes through the add below: FormData
    // carries the file, so the request is multipart and Shopify stores it as a line-item property.
    if (product.dataset.nativeForm === 'true') return;
    event.preventDefault();
    if (state.pending || state.uncertain || cartBusy || cartUnreconciled || product.dataset.customBlocked === 'true') return;
    const variant = state.variant;
    const message = product.querySelector('[data-product-status]');
    if (!variant) {
      // Point at the first unmade choice rather than reporting a generic failure.
      const missing = optionControls(product).find(control => !readOption(control));
      if (missing) {
        const name = missing.querySelector('legend,label')?.firstChild?.textContent?.trim() || '';
        // A product can word this itself (a gift card: "Choose an amount", not "Choose a amount").
        message.textContent = product.dataset.missingLabel || (name ? text.chooseMissing.replace('{{ name }}', name.toLowerCase()) : text.chooseOptions);
        const control = missing.querySelector('input,select') || missing;
        control.focus({ preventScroll: true });
        const calm = true; // Halloween theme: no smooth scrolling for anyone
        try { control.scrollIntoView({ block: 'center', behavior: calm ? 'auto' : 'smooth' }); } catch { /* non-visual host */ }
      } else message.textContent = text.unavailable;
      return;
    }
    const recipient = form.querySelector('[data-recipient-form]');
    if (variant.available && recipient && !checkRecipient(recipient)) { message.textContent = recipient.dataset.errorSummary; return; }
    if (!variant.available || !form.reportValidity()) { message.textContent = text.chooseOptions; return; }
    const artwork = form.querySelector('[data-artwork-field]');
    if (artwork && !checkArtwork(artwork, true)) {
      // The reason is under the field, which takes focus and is described by it, and is said aloud.
      message.textContent = '';
      announce(artwork.querySelector('[data-artwork-error]').textContent);
      const input = artwork.querySelector('[data-artwork-input]');
      input.focus({ preventScroll: true });
      const calm = true; // Halloween theme: no smooth scrolling for anyone
      try { input.scrollIntoView({ block: 'center', behavior: calm ? 'auto' : 'smooth' }); } catch { /* non-visual host */ }
      return;
    }
    const data = new FormData(form);
    data.set('id', String(variant.id)); data.set('sections', sectionNames().join(',')); data.set('sections_url', root.dataset.cartUrl);
    // Empty design notes are left off the order rather than sent as a blank line.
    const notes = form.querySelector('[data-design-notes]');
    if (notes && !notes.value.trim()) data.delete(notes.name);
    if (product.dataset.giftCard === 'true') giftCardProperties(data);
    // Read before either button is disabled for the request: that drops their focus to the page.
    const focusOrigin = addFocusOrigin(product);
    state.pending = true; lockCart(true);
    // The file and the notes are already in the request: while it runs they cannot be changed under it.
    const artworkControls = artwork ? [...form.querySelectorAll('[data-artwork-input],[data-design-notes]')] : [];
    artworkControls.forEach((control) => { control.disabled = true; });
    syncPlan(product);
    const add = product.querySelector('[data-add-button]'); add.disabled = true; add.setAttribute('aria-busy', 'true');
    message.textContent = addingText(product);
    let confirmed = false;
    // A refusal: the bag is re-read and the reason given, never replayed. A stock-limit error can still
    // add the available quantity, so the bag shows what really happened.
    const refuse = async (reason) => {
      try { await refreshCart(); addRecovery(product, reason); }
      catch { state.uncertain = true; cartRecovery(); addRecovery(product, reason + ' ' + text.cartRefreshFailed, true); }
    };
    try {
      const response = await fetch(`${root.dataset.addUrl}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: data });
      // A server error may still have added the line: it is settled below as an add that could not be confirmed.
      if (response.status >= 500) throw new Error(text.addUncertain);
      if (!response.ok) {
        // Every 4xx is a refusal, whatever its body: a 413 (a file too large for the store, turned away
        // before the bag is touched) or a page in front of the cart may not answer in JSON.
        const result = await response.json().catch(() => ({}));
        const tooLarge = response.status === 413;
        let errorMessage = tooLarge ? (artwork?.dataset.errorRefused || text.addFailed) : (result.description || result.message || text.addFailed);
        // A gift card's recipient refused by Shopify: the reasons go under the fields, the line says where.
        if (recipient && !tooLarge && showRecipientErrors(recipient, result.errors)) errorMessage = result.description || recipient.dataset.errorSummary;
        if (tooLarge && artwork) { artwork.querySelector('[data-artwork-input]').value = ''; checkArtwork(artwork, false); setArtworkError(artwork, errorMessage); }
        await refuse(errorMessage);
        return;
      }
      const result = await response.json();
      if (artwork && !uploadedArtwork(artwork, result, variant.id)) {
        // Shopify added the belt but kept no file. The line is not removed behind the shopper's back: the
        // bag is re-read, the field says the file didn't upload, and the status line says the belt in the
        // bag has no artwork, with the link to the bag. The chosen file stays for another try.
        lastCartChange = performance.now();
        setArtworkError(artwork, artwork.dataset.errorUpload);
        await refuse(artwork.dataset.errorUploadBag || artwork.dataset.errorUpload);
        return;
      }
      confirmed = true; lastCartChange = performance.now();
      if (recipient) resetRecipient(recipient);
      try { applyCartSections(result.sections); } catch { await refreshCart(); }
      message.textContent = text.added; status(text.added);
      // An add that came from the quick-select dialog finishes there: close it before the drawer
      // opens, so the shopper is not left with two stacked panels.
      const quickOrigin = product.closest('#QuickSelect');
      const returnTo = quickOrigin ? dialogOrigins.get(quickOrigin) : null;
      const shelf = quickOrigin ? quickShelf : null;
      if (quickOrigin) closeQuickSelect();
      if (shelf) focusAddedLine(shelf.where, variant.id);
      else openDialog(document.getElementById('CartDrawer'), returnTo || add);
    } catch {
      // An interrupted add can still have reached Shopify. Never automatically retry it: an add
      // that was not confirmed keeps this product locked until reload. Reconcile with a read so
      // the drawer shows the real bag and checkout stays usable; lock the cart only if that read
      // fails too. A confirmed add whose preview failed is known, so it is not locked.
      if (!confirmed) state.uncertain = true;
      try {
        await refreshCart();
        if (confirmed) { message.textContent = text.added; status(text.added); }
        else addRecovery(product, text.addUncertain, true);
        // Same hand-off as a clean add: the card dialog closes before the drawer opens, and focus
        // returns somewhere usable rather than to a disabled Add button.
        const quickOrigin = product.closest('#QuickSelect');
        const returnTo = quickOrigin ? dialogOrigins.get(quickOrigin) : null;
        const shelf = quickOrigin ? quickShelf : null;
        if (quickOrigin) closeQuickSelect();
        if (shelf) {
          // Chosen from the bag's suggestions: the bag is already open (or is the page), so the outcome
          // lands there, settled by the redrawn bag itself.
          if (confirmed || lineIn(shelf.where, variant.id)) focusAddedLine(shelf.where, variant.id);
          else focusShelf(shelf.where, shelf.rec, text.addUncertain);
        } else {
          openDialog(document.getElementById('CartDrawer'), returnTo || (state.uncertain ? message.querySelector('a') : null) || add);
          // The drawer is modal, so the product's own status line is out of reach: repeat the
          // warning beside the bag it refers to.
          if (!confirmed) cartMessage(text.addUncertain);
        }
      } catch {
        cartRecovery();
        addRecovery(product, confirmed ? text.addedRefreshFailed : text.addUncertain, true);
      }
    } finally {
      if (!confirmed) cancelCheckout();
      state.pending = false; lockCart(false); add.removeAttribute('aria-busy'); add.disabled = state.uncertain || !state.variant?.available;
      artworkControls.forEach((control) => { control.disabled = false; });
      syncPlan(product);
      settleAddFocus(focusOrigin, product);
    }
  });
  // ---- Card quick select -------------------------------------------------------------------
  // The card's Add to cart opens this when the product has real choices. The dialog body is the
  // product's own quick-select section, so the chips, the availability marking, the variant
  // resolution, the add request and the cart drawer are the ones the product page already uses.
  const quickCache = new Map();
  let quickToken = 0;
  // The sticky Add row covers the bottom of the panel's scroller. Its measured height becomes the
  // panel's scroll-padding-bottom (--quick-actions in theme.css), so a size reached by Tab or the
  // arrow keys, or picked out by validation, is scrolled clear of the row, not left underneath it.
  let quickActionsSize = null;
  const trackQuickActions = (dialog, host) => {
    quickActionsSize?.disconnect();
    const actions = host.querySelector('.purchase-actions');
    if (!actions || !('ResizeObserver' in window)) return;
    quickActionsSize = new ResizeObserver(() => dialog.style.setProperty('--quick-actions', `${Math.ceil(actions.getBoundingClientRect().height)}px`));
    quickActionsSize.observe(actions);
  };
  // Where a quick-select dialog was opened from the bag's suggestions ('drawer' | 'page'), so its add
  // can land back in the bag instead of opening another panel.
  let quickShelf = null;
  async function openQuickSelect(button) {
    const where = shelfOf(button);
    quickShelf = where ? { where, rec: button.closest('[data-cart-rec]')?.dataset.productId } : null;
    // Which suggestion opened it, for the hidden _suggested line property: the bag's suggestions or a product
    // page's "Complete the set" pairs (snippets/pairs-with). A product card's dialog carries none.
    const suggested = where ? 'bag' : button.closest('[data-pairs]') ? 'pairs' : '';
    const dialog = document.getElementById('QuickSelect');
    const host = dialog?.querySelector('[data-quick-select-content]');
    if (!dialog || !host) { location.href = button.dataset.quickSelect; return; }
    const token = ++quickToken;
    // The heading stays "Choose your options" — the panel's own product block carries the
    // identity, and renaming the dialog to the product duplicated the title on screen.
    const loading = document.createElement('p');
    loading.className = 'form-status';
    loading.setAttribute('role', 'status');
    loading.textContent = button.dataset.quickTitle ? `${button.dataset.quickTitle} — ${text.quickLoading}` : text.quickLoading;
    host.replaceChildren(loading);
    openDialog(dialog, button);
    const url = new URL(button.dataset.quickSelect, location.origin);
    url.searchParams.set('section_id', 'quick-select');
    try {
      let markup = quickCache.get(url.href);
      if (markup === undefined) {
        const response = await fetch(url, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(String(response.status));
        markup = await response.text();
        quickCache.set(url.href, markup);
      }
      // A slower earlier request must never overwrite the dialog the shopper is now looking at.
      if (token !== quickToken || !dialog.open) return;
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const body = parsed.querySelector('[data-quick-body]');
      if (!body) throw new Error('quick-select body missing');
      const incoming = [body, ...parsed.querySelectorAll('dialog.guide-dialog')].map(node => node.cloneNode(true));
      host.replaceChildren(...incoming);
      host.dataset.thickness = url.searchParams.get('thickness') || '';
      init(host);
      // Opened from the bag's suggestions or a product page's pairs: mark the add (hidden in cart and checkout,
      // kept on the order).
      if (suggested) host.querySelector('form.product-form')?.append(Object.assign(document.createElement('input'), { type: 'hidden', name: 'properties[_suggested]', value: suggested }));
      trackQuickActions(dialog, host);
      // The no-JS variant select comes first in the markup but sits in a hidden wrapper; only a
      // control with a box on screen may take focus. With none, focus stays on Close.
      const first = [...host.querySelectorAll('input:not([type=hidden]),select:not([hidden]),button')].find(control => control.getClientRects().length);
      first?.focus({ preventScroll: true });
    } catch {
      if (token !== quickToken || !dialog.open) return;
      const failed = document.createElement('p');
      failed.className = 'notice';
      failed.textContent = text.quickFailed;
      const link = document.createElement('a');
      link.className = 'button button-small button-block';
      link.href = button.dataset.quickSelect;
      link.textContent = text.chooseOptions;
      host.replaceChildren(failed, link);
    }
  }
  // Close the quick-select dialog once its add has landed, so the cart drawer is not stacked
  // behind a selection panel the shopper has finished with.
  function closeQuickSelect() {
    const dialog = document.getElementById('QuickSelect');
    if (dialog?.open) dialog.close();
  }

  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-product]').forEach(product => products.get(product)?.controller?.abort());
    event.target.querySelectorAll('dialog[open]').forEach(closeDialog);
  });
  window.addEventListener('pageshow', async event => {
    // Without the back-forward cache a Back visit can still restore the page from disk cache, with the
    // header count and bag as they were. Refresh on either kind of history return.
    const history = window.performance?.getEntriesByType?.('navigation')?.[0]?.type === 'back_forward';
    // Back from checkout: the page that left is live again, so a new checkout decision may leave too.
    if (event.persisted) { checkoutLeaving = false; cancelCheckout(); }
    if ((!event.persisted && !history) || cartBusy) return;
    lockCart(true);
    try { await trackCart(refreshCart()); } catch { cartRecovery(); cartMessage(text.cartRefreshFailed); } finally { lockCart(false); }
  });
  // Keyboard: disabling the focused checkout button drops focus to <body> (Chrome fires focusout with
  // no destination), so Enter or Space then reached nothing. Focus moves to the wrapper instead, and
  // those keys there make the same decision as a tap. The button itself is never touched.
  document.addEventListener('focusout', event => {
    const button = event.target;
    if (event.relatedTarget || !button.matches?.('[data-checkout]') || !button.disabled || button.hasAttribute('data-blocked')) return;
    const hold = button.closest('[data-checkout-hold]');
    if (hold) { hold.tabIndex = -1; hold.focus({ preventScroll: true }); }
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const active = document.activeElement;
    const hold = active?.matches?.('[data-checkout-hold]') ? active : active?.matches?.('[data-checkout]:disabled:not([data-blocked])') ? active.closest('[data-checkout-hold]') : null;
    const button = hold?.querySelector('[data-checkout]');
    if (!button?.form) return;
    event.preventDefault();
    requestCheckout(button.form);
  });
  // Carry on a checkout the options app interrupted with its reload: only on that reload (a plain
  // reload on /cart, ?mepcRefresh elsewhere) and only within the original wait window.
  try {
    const at = Number(sessionStorage.getItem(INTENT_KEY));
    sessionStorage.removeItem(INTENT_KEY);
    const age = Date.now() - at;
    const appReload = window.performance?.getEntriesByType?.('navigation')?.[0]?.type === 'reload' || new URLSearchParams(location.search).has('mepcRefresh');
    const form = document.querySelector('[data-main-cart] [data-cart-form]') || document.querySelector('#CartDrawer [data-cart-form]');
    if (at && appReload && age < CHECKOUT_WAIT_LIMIT && form) {
      if (!form.closest('[data-main-cart]')) openDialog(document.getElementById('CartDrawer'));
      checkoutIntent = { formId: form.id, since: performance.now() - age };
      rememberIntent(at);
      cartMessage(text.checkoutPending);
      settleCheckout();
    }
  } catch { /* storage unavailable: the shopper taps again */ }
  // ---- Announcement strip ------------------------------------------------------------------
  // One message at a time, moved only by the shopper. With a single enabled message the controls
  // stay hidden. Nothing rotates on its own. The first paint already matches this state (theme.css
  // shows only the first message under html.js until the list is marked ready, and the header
  // renders Previous and Next visible when there is more than one message), so taking over here
  // moves nothing. Re-run when the theme editor re-renders the header.
  const initAnnouncement = (root) => {
    const announcement = root.querySelector('[data-announcement]');
    if (!announcement) return;
    const items = [...announcement.querySelectorAll('[data-announcement-item]')];
    const prev = announcement.querySelector('[data-announcement-prev]');
    const next = announcement.querySelector('[data-announcement-next]');
    const list = announcement.querySelector('[data-announcement-list]');
    if (items.length > 1 && prev && next && list) {
      let index = 0;
      list.setAttribute('aria-live', 'polite');
      const show = (target) => { index = (target + items.length) % items.length; items.forEach((item, i) => { item.hidden = i !== index; }); };
      prev.hidden = false; next.hidden = false;
      prev.addEventListener('click', () => show(index - 1));
      next.addEventListener('click', () => show(index + 1));
      show(0);
    }
    if (list) list.setAttribute('data-announcement-ready', '');
  };
  initAnnouncement(document);
  document.addEventListener('shopify:section:load', (event) => initAnnouncement(event.target));
  // ---- Review excerpts ------------------------------------------------------------------------
  // A Read full review control appears only when the collapsed excerpt actually hides text; the
  // full review is already in the card's dialog, opened through the shared dialog handling.
  const excerpts = [...document.querySelectorAll('[data-proof-excerpt]')];
  if (excerpts.length) {
    const measure = () => {
      for (const excerpt of excerpts) {
        const paragraph = excerpt.querySelector('p');
        const button = excerpt.parentElement.querySelector('[data-open-dialog]');
        if (!paragraph || !button) continue;
        button.hidden = paragraph.scrollHeight <= paragraph.clientHeight + 2;
      }
    };
    measure();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    let timer;
    window.addEventListener('resize', () => { clearTimeout(timer); timer = setTimeout(measure, 150); });
  }
  // ---- Footer vs the chat launcher ---------------------------------------------------------------
  // Shopify Inbox's launcher floats at a bottom corner; its side, size and label are app settings, so
  // nothing is assumed. Two measurements, both 16px clear of the launcher's real box:
  //   --chat-reach-right / --chat-reach-left  how far the launcher reaches in from its side of the
  //     window. From 768px the footer (signup band included) uses the site shell, and theme.css ends
  //     its content that much sooner on that side where the shell's margin is narrower (768-999px only
  //     the signup form is capped; from 1000px the launcher steps aside at the footer instead, and the
  //     right side ends short of it only on a page too short to scroll the footer away, .chat-indent),
  //     so no footer control (Sign up, a group link, Update) passes under the launcher.
  //   --chat-safe  the widest centred column whose edges both stay clear of it: the phone layout's
  //     cap (600-767px). Where that leaves under 320px (phones) the cap is dropped and the page end
  //     keeps the launcher's height free under the last row instead (theme.css).
  // Measured from the real button: as soon as the app has drawn it (it arrives after load), on resize,
  // and when its own box changes. Writing the cap never moves the launcher, so nothing loops.
  // The launcher button is the app's "activator" part, inside nested open shadow roots of <shopify-chat>.
  const findLauncher = (root) => {
    for (const node of root.querySelectorAll('*')) {
      if (node.matches('button[part~="activator"]')) return node;
      const inner = node.shadowRoot && findLauncher(node.shadowRoot);
      if (inner) return inner;
    }
    return null;
  };
  const chatLauncher = () => { const host = document.querySelector('shopify-chat'); return host?.shadowRoot ? findLauncher(host.shadowRoot) : null; };
  {
    const GAP = 16;
    const MIN = 320;
    const html = document.documentElement;
    let launcher = null;
    let watched = null;
    let frame = 0;
    let applied = '';
    let reach = '';
    const fit = () => {
      frame = 0;
      if (!launcher?.isConnected) launcher = chatLauncher();
      if (launcher && launcher !== watched && 'ResizeObserver' in window) { watched = launcher; new ResizeObserver(() => schedule()).observe(launcher); }
      const box = launcher?.getBoundingClientRect();
      // A launcher the app has hidden (its panel open, a hand-off) measures 0x0: keep the last real fit.
      if (launcher && box && (!box.width || !box.height)) return;
      // Its height also sets how far focus scrolls clear of the bottom while the sticky bar lifts it (theme.css).
      if (box && box.height) html.style.setProperty('--chat-launcher-h', `${Math.ceil(box.height)}px`);
      const width = html.clientWidth;
      let safe = 0;
      let side = '';
      let inset = 0;
      if (box && box.width && box.height) {
        const right = box.left + box.width / 2 > width / 2;
        safe = right ? 2 * (box.left - GAP) - width : width - 2 * (box.right + GAP);
        side = right ? 'right' : 'left';
        inset = Math.max(0, Math.ceil(right ? width - box.left + GAP : box.right + GAP));
      }
      const value = safe >= MIN ? `${Math.floor(safe)}px` : '';
      if (value !== applied) {
        applied = value;
        if (value) html.style.setProperty('--chat-safe', value); else html.style.removeProperty('--chat-safe');
      }
      const reachValue = side ? `${side}:${inset}px` : '';
      if (reachValue !== reach) {
        reach = reachValue;
        html.style.removeProperty('--chat-reach-right');
        html.style.removeProperty('--chat-reach-left');
        if (side) html.style.setProperty(`--chat-reach-${side}`, `${inset}px`);
      }
      tuck();
    };
    // Phones: the column can't be narrowed around the launcher, so while the footer is on screen the
    // launcher button steps out of view (owner brief: hide or adapt the overlay rather than let it sit on
    // the signup, the group buttons, the region selector or Update). It comes back as soon as the footer
    // scrolls away. Only the launcher button part is hidden, never an open chat panel.
    let footerOn = false;
    // From 1000px too (owner 9 Oct): the footer and the signup band now run to equal gutters on both sides
    // instead of ending short of the launcher, so the launcher steps aside there as it does on phones.
    const wide = typeof window.matchMedia === 'function' ? window.matchMedia('(min-width: 1000px)') : null;
    // Never while the launcher holds any focus (hiding it would drop that focus to the page); re-checked when
    // focus arrives anywhere and when it leaves, so a launcher that let go of focus steps away again.
    // A page too short to scroll the footer away (404, a near-empty bag) would never show chat again on
    // desktop: there the launcher stays and the footer's right side ends short of it (.chat-indent).
    function tuck() {
      const desk = !!wide?.matches;
      const footer = desk ? document.querySelector('.site-footer') : null;
      const stays = !!footer && footer.getBoundingClientRect().top + window.scrollY <= window.innerHeight;
      html.classList.toggle('chat-indent', stays);
      html.classList.toggle('chat-tucked', footerOn && (!applied || (desk && !stays)) && !!launcher && !document.activeElement?.closest?.('shopify-chat'));
    }
    document.addEventListener('focusin', () => tuck());
    document.addEventListener('focusout', () => setTimeout(tuck, 0));
    function schedule() { if (!frame) frame = (document.hidden || typeof requestAnimationFrame !== 'function') ? setTimeout(fit, 0) : requestAnimationFrame(fit); }
    // The footer coming into view (or leaving it) also re-measures the launcher, so the room kept for it is
    // fresh whenever it matters, whatever the app did to its button since.
    if ('IntersectionObserver' in window) {
      const footerWatch = new IntersectionObserver((entries) => { for (const entry of entries) footerOn = entry.isIntersecting; tuck(); schedule(); });
      const watchFooter = () => { footerWatch.disconnect(); const footer = document.querySelector('.site-footer'); if (footer) footerWatch.observe(footer); };
      watchFooter();
      document.addEventListener('shopify:section:load', watchFooter);
    }
    // The app draws its launcher after load and scales it into place (on the 1440px preview it first
    // measures 56x30, then 93x50, with no size change a ResizeObserver reports): keep measuring every 500ms
    // until it is found and its box has held still for two checks in a row (or 20s pass without one).
    let tries = 0;
    let still = 0;
    let lastBox = '';
    const wait = setInterval(() => {
      fit();
      const box = launcher?.isConnected ? launcher.getBoundingClientRect() : null;
      const key = box && box.width && box.height ? `${box.left},${box.top},${box.width},${box.height}` : '';
      still = key && key === lastBox ? still + 1 : 0;
      lastBox = key;
      if (still >= 2 || ++tries > 40) clearInterval(wait);
    }, 500);
    window.addEventListener('resize', schedule);
    // On a phone the column cannot be narrowed around the launcher, so a control reached by keyboard can
    // land under it (the region selector and Update sit at the launcher's height). Scroll the page just
    // enough to bring a focused control clear of it; nothing moves for focus that is already clear.
    const CONTROL = 'a[href],button,input,select,textarea,summary,[role="button"],[role="radio"],[role="checkbox"]';
    document.addEventListener('focusin', (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.matches(CONTROL) || target.closest('shopify-chat, dialog') || html.classList.contains('chat-tucked')) return;
      // Keyboard only: a mouse or tap focus must not move the page between press and release.
      let keyboard = false;
      try { keyboard = target.matches(':focus-visible'); } catch { keyboard = false; }
      if (!keyboard) return;
      const box = launcher?.isConnected ? launcher.getBoundingClientRect() : null;
      if (!box || !box.width || !box.height) return;
      const r = target.getBoundingClientRect();
      if (r.right <= box.left || r.left >= box.right || r.bottom <= box.top || r.top >= box.bottom) return;
      if (r.height >= box.top - GAP) return;
      window.scrollBy({ top: Math.ceil(r.bottom - box.top + GAP), behavior: 'auto' });
    });
    fit();
  }
  // ---- Cookie preferences ----------------------------------------------------------------------
  // Shopify's cookie banner (loaded store-wide) offers window.privacyBanner.showPreferences(). The footer's
  // Cookie preferences control stays hidden until that exists, then opens Shopify's own dialog; the
  // theme never records a choice itself.
  {
    const ready = () => typeof window.privacyBanner?.showPreferences === 'function';
    const reveal = () => { const ok = ready(); document.querySelectorAll('[data-cookie-preferences]').forEach(item => { item.hidden = !ok; }); return ok; };
    // The banner script loads async and can land well after this one on a slow connection.
    if (!reveal()) { let tries = 0; const wait = setInterval(() => { if (reveal() || ++tries > 120) clearInterval(wait); }, 500); }
    window.addEventListener('load', reveal);
    document.addEventListener('shopify:section:load', reveal);
    document.addEventListener('click', event => {
      if (event.target.closest?.('[data-cookie-preferences] button') && ready()) window.privacyBanner.showPreferences();
    });
  }
  // ---- Newsletter --------------------------------------------------------------------------------
  // The signup is Shopify's customer form and posts natively; Shopify answers with the page again
  // (its error, or the confirmation). This only replaces the browser's validation bubble: an empty
  // or malformed address gets the form's own message line (tied to the field and read out) and is
  // not sent. Without this script the browser's required/email validation still applies.
  // Shopify's injected form-persister listens for the same submit on every /contact form and then
  // calls form.submit() itself, which preventDefault() cannot stop. So this listener runs first (capture
  // phase) and ends the event for an invalid address; a valid one is left alone and goes through the
  // persister, which adds the form key Shopify's bot check needs.
  for (const wrap of document.querySelectorAll('[data-newsletter]')) {
    const form = wrap.querySelector('form');
    const input = wrap.querySelector('input[type="email"]');
    const message = wrap.querySelector('[data-newsletter-status]');
    if (!form || !input || !message) continue;
    form.noValidate = true;
    const report = (problem) => {
      message.textContent = problem;
      message.classList.toggle('is-error', !!problem);
      message.classList.remove('is-success');
      message.removeAttribute('tabindex');
      if (problem) input.setAttribute('aria-invalid', 'true'); else input.removeAttribute('aria-invalid');
    };
    form.addEventListener('submit', (event) => {
      if (input.validity.valid) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      report(input.validity.valueMissing ? wrap.dataset.errorMissing : wrap.dataset.errorInvalid);
      input.focus();
    }, true);
    input.addEventListener('input', () => { if (input.getAttribute('aria-invalid') === 'true' && input.validity.valid) report(''); });
  }
  // ---- Recent-orders pop ---------------------------------------------------------------------------
  // #sro-popup belongs to the Show Recent Orders app: its script owns the message, the timing, the
  // slide in and out (it rewrites the inline bottom frame by frame) and the close button, and nothing
  // here writes to it. The theme only moves the box, with CSS, from two things it reads off the page:
  //   .sro-aside  on <html> while the place the pop will come to rest would cover a hero action, a buy
  //               control or whatever a keyboard user has focused. theme.css then moves it out of view,
  //               as it already does for an open dialog. It is judged from the first frame the pop is on
  //               screen, in the same task as the app's own style write, so it never paints over the control.
  //   --sro-lift  on <html>, a length: how far the pop rides up to clear the sticky buy bar (phones and
  //               tablets) and the Shopify Inbox launcher. It follows the app's slide, 0 while the pop is
  //               wholly below the screen and the full lift at rest, so the app's slide in, slide out and
  //               hidden state stay its own. (A fixed `bottom: ... !important` used here before kept the pop
  //               on screen above the bar after the app had hidden it.)
  // The launcher: on a 390px phone the full-width pop rested right over it (93x50 at the bottom right) for its
  // whole stay, on product pages (both above the bar) and on collections (8 Oct). Where the pop's resting box
  // would overlap the launcher's, the pop rides on up until it rests above the launcher; a launcher that is not
  // on screen (none, hidden by its app, tucked away at the footer) changes nothing. The .sro-aside guard is
  // judged at that higher resting place, so the pop still never rests over a hero action or a buy control.
  {
    const guarded = '.hero-actions a,[data-add-button],[data-buy-bar-button],[data-quick-select],[data-quick-add] button,[data-checkout],[data-accelerated]';
    const REST = 10;      // where the app parks the pop (its inline bottom); a floor, so a slide-in is judged by where it ends
    const BAR_GAP = 4;    // air between the pop and the top of the sticky buy bar
    const CHAT_GAP = 8;   // air between the pop and the chat launcher, whose focus ring reaches 5px beyond it
    const RING = 6;       // a focus ring reaches 3px + 3px offset beyond its control
    const html = document.documentElement;
    let pop = null;
    let appliedLift = '';
    let chat = null;
    // The launcher's box while it is on screen and drawn, else null.
    const launcherBox = () => {
      if (!chat?.isConnected) chat = chatLauncher();
      if (!chat) return null;
      const r = chat.getBoundingClientRect();
      if (!r.width || !r.height || r.bottom <= 0 || r.top >= window.innerHeight) return null;
      const style = getComputedStyle(chat);
      return style.visibility === 'hidden' || style.display === 'none' ? null : r;
    };
    // Where the pop is, or is sliding to. null while it is wholly below the screen (or not placed yet),
    // when there is nothing to cover and nothing to lift. Otherwise its resting box, which is also the
    // highest place it will reach, so a control clear of it is clear for the whole slide.
    const place = () => {
      if (!pop || !pop.isConnected || getComputedStyle(pop).display === 'none') return null;
      const now = parseFloat(pop.style.bottom);
      const { left, right, height } = pop.getBoundingClientRect();
      if (!Number.isFinite(now) || !height || now <= -height) return null;
      // The bar shows on phones and desktop; one that is not rendered (display:none) has no height.
      // An open dialog does not change the bar's part of the lift: theme.css moves the pop off screen while
      // one is open. The launcher drops back to its corner meanwhile, so the lift is measured again when the
      // dialog closes (the class watch below).
      const shown = document.querySelector('.buy-bar[data-buy-bar]:not([hidden]):not(.is-stowed)');
      const bar = shown && shown.offsetHeight ? shown : null;
      let lift = bar ? Math.max(0, bar.offsetHeight + BAR_GAP - REST) : 0;
      // theme.css lifts the launcher above the bar too, so it is measured where it actually is.
      const launcher = launcherBox();
      if (launcher && left < launcher.right && right > launcher.left) {
        const restBottom = window.innerHeight - REST - lift;
        const clear = launcher.top - CHAT_GAP;
        if (restBottom > clear && restBottom - height < launcher.bottom) lift += restBottom - clear;
      }
      const top = window.innerHeight - (Math.max(now, REST) + lift) - height;
      const risen = Math.min(1, (now + height) / (REST + height));
      return { left, right, top, bottom: top + height, lift: lift * risen };
    };
    // The control a keyboard user is on, looking through open shadow roots: the Inbox launcher is a
    // custom element whose button sits inside nested roots, and document.activeElement is only its host.
    const focusedControl = () => {
      let node = document.activeElement;
      while (node && node.shadowRoot && node.shadowRoot.activeElement) node = node.shadowRoot.activeElement;
      if (!node || node === document.body) return null;
      try { return node.matches(':focus-visible') ? node : null; } catch { return node; /* no :focus-visible: treat any focus as keyboard focus */ }
    };
    // Controls inside a dialog sit in the top layer, above the pop.
    const covers = (box, node, slack) => {
      if (!node || node.closest('dialog')) return false;
      const r = node.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && r.left - slack < box.right && r.right + slack > box.left && r.top - slack < box.bottom && r.bottom + slack > box.top;
    };
    const setLift = (px) => {
      const value = `${Math.round(px * 100) / 100}px`;
      if (value === appliedLift) return;
      appliedLift = value;
      html.style.setProperty('--sro-lift', value);
      html.classList.toggle('sro-lifted', px > 0);
    };
    const check = () => {
      const spot = place();
      let aside = false;
      if (spot) aside = covers(spot, focusedControl(), RING) || [...document.querySelectorAll(guarded)].some((node) => covers(spot, node, 0));
      html.classList.toggle('sro-aside', aside);
      setLift(spot ? spot.lift : 0);
    };
    // The app moves the pop by rewriting its inline style; follow that in the same task, never change it.
    const watch = (node) => {
      if (node === pop) return;
      pop = node;
      new MutationObserver(check).observe(node, { attributes: true, attributeFilter: ['style'] });
      check();
    };
    // The app appends the pop to <body> some seconds after load.
    new MutationObserver((records) => {
      for (const record of records) for (const node of record.addedNodes) if (node.id === 'sro-popup') watch(node);
    }).observe(document.body, { childList: true });
    const existing = document.getElementById('sro-popup');
    if (existing) watch(existing);
    // The sticky buy bar stows and returns as the Add button leaves and re-enters the screen, which moves
    // the pop's resting place (and its lift) without the app writing anything.
    const buyBar = document.querySelector('.buy-bar[data-buy-bar]');
    if (buyBar) new MutationObserver(check).observe(buyBar, { attributes: true, attributeFilter: ['class', 'hidden'] });
    // Scroll, resize and focus also run it in the task that reports them (scroll events are delivered
    // before the frame's paint), so a control that moves under the resting pop is never painted over.
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    document.addEventListener('focusin', check);
    document.addEventListener('focusout', check);
    // The launcher steps out of view while the footer is on screen (.chat-tucked) and drops back to its corner
    // while a dialog is open (.dialog-open): both move the place the pop has to clear. (Only those two classes:
    // check() itself writes .sro-aside and .sro-lifted on the same element.)
    const moves = (classes) => ['chat-tucked', 'dialog-open'].map((name) => ` ${classes || ''} `.includes(` ${name} `)).join();
    new MutationObserver((records) => {
      if (records.some((record) => moves(record.oldValue) !== moves(html.className))) check();
    }).observe(html, { attributes: true, attributeFilter: ['class'], attributeOldValue: true });
  }
  init();
// Related products. Fetched rather than server-rendered so the PDP's own HTML stays lean and
  // an empty recommendation set costs nothing. Failure is silent by design: a missing
  // suggestion strip must never look like a broken page.
  // First the products the store pins as going with this one (Search & Discovery complementary); a
  // product with none shows related gear instead, under its own heading (the section picks it by intent).
  const recommendations = document.querySelector('[data-product-recommendations]');
  if (recommendations && recommendations.dataset.url) {
    const load = (intent) => fetch(`${recommendations.dataset.url}&intent=${intent}`)
      .then(response => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
      .then(markup => new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-product-recommendations]')?.innerHTML.trim() || '');
    load('complementary').catch(() => '').then(html => html || load('related')).then(html => { if (html) recommendations.innerHTML = html; }).catch(() => {});
  }

})();

// ---- Logo rail ("Worn at meets") ---------------------------------------------------------------
// A separate guarded block: the rail must keep working when the theme editor re-renders its
// section, and it must never take the purchase runtime above down with it. Vanilla, no library.
// Motion is one Web Animations API transform on the track with explicit pixel keyframes. When the
// loop width, the number of copies, the speed or the device pixel ratio changes, the animation is
// rebuilt and the current phase (fraction of a loop) is carried over, so a resize never jumps.
//   - copies of the list fill the viewport: aria-hidden, inert, alt="", nothing focusable
//   - the loop waits until every mark has decoded (data-ready); only then does the viewport clip
//   - one loop is rounded up to a whole CSS pixel that is also a whole device pixel (--pl-round)
//   - Pause/Play is always available; Pause shows every mark as a still grid and is remembered
//   - offscreen, hidden tab, a held finger, mouse hover, focus inside the rail and theme-editor
//     selection freeze the loop in place (no layout change)
//   - prefers-reduced-motion and the section's Motion setting: no copies, no animation
(() => {
  'use strict';
  if (window.akinciProofRail) return;
  // Without these the list stays a row that scrolls by hand, which is the designed fallback.
  if (typeof window.matchMedia !== 'function' || typeof window.IntersectionObserver !== 'function'
    || typeof window.ResizeObserver !== 'function' || typeof Element.prototype.animate !== 'function') return;

  const SELECTOR = '[data-proof-rail]';
  const STORE_KEY = 'akinci:proof-rail:paused';
  const HOLDS = ['offscreen', 'tab-hidden', 'hold', 'hover', 'focus', 'editor-section', 'editor-block'];
  const controllers = new WeakMap();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const readPaused = () => { try { return window.localStorage.getItem(STORE_KEY) === '1'; } catch { return false; } };
  const writePaused = (value) => {
    try { if (value) window.localStorage.setItem(STORE_KEY, '1'); else window.localStorage.removeItem(STORE_KEY); } catch { /* storage blocked: lasts for this page */ }
  };
  const onChange = (query, handler, signal) => { if (typeof query.addEventListener === 'function') query.addEventListener('change', handler, { signal }); };

  function setup(root) {
    if (root.hasAttribute('data-static')) return null;
    const viewport = root.querySelector('[data-proof-rail-viewport]');
    const track = root.querySelector('[data-proof-rail-track]');
    const group = root.querySelector('[data-proof-rail-group]');
    const toggle = root.querySelector('[data-proof-rail-toggle]');
    const label = toggle && toggle.querySelector('[data-proof-rail-label]');
    if (!viewport || !track || !group || !toggle || !label) return null;

    const abort = new AbortController();
    const { signal } = abort;
    const text = { pause: toggle.dataset.labelPause || 'Pause', play: toggle.dataset.labelPlay || 'Play' };
    const holds = new Set(['starting']);   // keeps the first frame still until the copies have decoded
    let paused = readPaused() || root.hasAttribute('data-paused');
    let anim = null;
    let loop = 0;       // px per loop (one list, snapped)
    let copies = 0;     // lists in the track, the real one included
    let speed = 0;      // px per second
    let ratio = 0;      // devicePixelRatio the loop was snapped for
    let started = false;
    let destroyed = false;

    track.querySelectorAll('[data-proof-rail-clone]').forEach((node) => node.remove());

    const moving = () => started && !paused && !reduceMotion.matches;
    const readSpeed = () => {
      const style = getComputedStyle(root);
      return (parseFloat(style.getPropertyValue('--pl-speed')) || 150) * (parseFloat(style.getPropertyValue('--pl-speed-scale')) || 1);
    };
    const dpr = () => window.devicePixelRatio || 1;
    // Smallest whole CSS px >= w that is also a whole number of device pixels (DPR 1.5: even px,
    // 1.25: multiples of 4), so every copy rasterises on the same pixel phase.
    const snap = (w) => {
      const r = dpr();
      let n = Math.ceil(w - 0.001);
      for (let k = 0; k < 64; k++, n++) if (Math.abs(n * r - Math.round(n * r)) < 0.005) return n;
      return Math.ceil(w - 0.001);
    };
    const duration = () => (anim ? anim.effect.getTiming().duration : 0);
    const phase = () => {
      if (!anim || anim.currentTime == null) return 0;
      const d = duration();
      return (((anim.currentTime % d) + d) % d) / d;
    };

    const makeClone = () => {
      const clone = group.cloneNode(true);
      clone.removeAttribute('data-proof-rail-group');
      clone.removeAttribute('role');
      clone.setAttribute('data-proof-rail-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((node) => node.removeAttribute('data-shopify-editor-block'));
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) => node.setAttribute('tabindex', '-1'));
      clone.querySelectorAll('img').forEach((img) => { img.alt = ''; img.loading = 'eager'; });
      return clone;
    };
    const setCopies = (n) => {
      const clones = track.querySelectorAll('[data-proof-rail-clone]');
      for (let i = clones.length + 1; i < n; i++) track.append(makeClone());
      for (let i = clones.length + 1; i > n; i--) track.lastElementChild.remove();
    };

    const sync = () => {
      for (const name of HOLDS) root.toggleAttribute(`data-${name}`, holds.has(name));
      if (!anim) return;
      if (holds.size) { if (anim.playState !== 'paused') anim.pause(); }
      else if (anim.playState !== 'running') anim.play();
    };

    const build = (fraction) => {
      if (anim) { anim.cancel(); anim = null; }
      if (!moving() || !loop) return;
      const ms = (loop / speed) * 1000;
      anim = track.animate(
        [{ transform: 'translate3d(0, 0, 0)' }, { transform: `translate3d(${-loop}px, 0, 0)` }],
        { duration: ms, iterations: Infinity, easing: 'linear' },
      );
      anim.currentTime = fraction * ms;
      sync();
    };

    // Measure, snap, fill the viewport with copies, and rebuild only when something changed.
    const layout = () => {
      if (destroyed || !started) return;
      if (!moving()) {
        if (anim) { anim.cancel(); anim = null; }
        loop = 0;
        return;
      }
      const pad = parseFloat(root.style.getPropertyValue('--pl-round')) || 0;
      const base = group.getBoundingClientRect().width - pad;
      if (!base) return;
      const target = snap(base);
      const round = target - base;
      if (Math.abs(round - pad) > 0.0005) root.style.setProperty('--pl-round', `${round.toFixed(6)}px`);
      const need = Math.max(2, Math.ceil(viewport.clientWidth / target) + 1);
      const nextSpeed = readSpeed();
      const fraction = phase();
      if (need !== copies) { setCopies(need); copies = need; }
      if (target !== loop || nextSpeed !== speed || dpr() !== ratio || !anim) {
        loop = target; speed = nextSpeed; ratio = dpr();
        build(fraction);
      }
    };

    // Bring a real mark fully into view (keyboard focus, or the merchant selecting its block).
    const reveal = (node) => {
      if (!anim || !loop || !node || !group.contains(node)) return;
      const item = node.closest('.proof-rail__item') || node;
      const box = viewport.getBoundingClientRect();
      const style = getComputedStyle(viewport);
      const start = box.left + (parseFloat(style.paddingLeft) || 0);
      const end = box.right - (parseFloat(style.paddingRight) || 0);
      const rect = item.getBoundingClientRect();
      if (rect.left >= start && rect.right <= end) return;
      const offset = rect.left - group.getBoundingClientRect().left;
      anim.currentTime = (Math.min(Math.max(offset, 0), loop) / loop) * duration();
    };

    const render = () => {
      root.toggleAttribute('data-paused', paused && !reduceMotion.matches);
      label.textContent = paused ? text.play : text.pause;
    };

    toggle.addEventListener('click', () => {
      paused = !paused;
      writePaused(paused);
      render();
      if (anim) { anim.cancel(); anim = null; }
      loop = 0;         // Play restarts from the first mark, the one the grid showed first
      layout();
    }, { signal });
    // Pause works from here, before the marks finish loading; show the button now so a quick
    // keyboard user cannot tab past a hidden control.
    root.setAttribute('data-armed', '');

    const hold = (name, on) => { if (on) holds.add(name); else holds.delete(name); sync(); };

    // Offscreen and hidden tab: no work while nobody can see it.
    const onscreen = new IntersectionObserver((entries) => {
      for (const entry of entries) hold('offscreen', !entry.isIntersecting);
    });
    onscreen.observe(root);
    const syncHidden = () => hold('tab-hidden', document.hidden);
    document.addEventListener('visibilitychange', syncHidden, { signal });
    syncHidden();

    // Touch / pen: a held finger freezes it; lifting it, or the page taking the gesture as a vertical
    // scroll (pointercancel), lets go. Mouse: hovering freezes it on fine pointers only.
    viewport.addEventListener('pointerdown', (event) => { if (event.pointerType !== 'mouse') hold('hold', true); }, { signal });
    for (const type of ['pointerup', 'pointercancel', 'pointerleave']) {
      viewport.addEventListener(type, (event) => { if (event.pointerType !== 'mouse') hold('hold', false); }, { signal });
    }
    viewport.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse' && finePointer.matches) hold('hover', true); }, { signal });
    viewport.addEventListener('pointerleave', (event) => { if (event.pointerType === 'mouse') hold('hover', false); }, { signal });
    // Focus inside the moving list (a mark with a link) freezes it and brings the focused mark into
    // view, so it never slides under the edge. The button sits outside the viewport.
    viewport.addEventListener('focusin', (event) => { hold('focus', true); reveal(event.target); }, { signal });
    viewport.addEventListener('focusout', (event) => { if (!viewport.contains(event.relatedTarget)) hold('focus', false); }, { signal });

    // Theme editor: hold still while the merchant has this section or one of its logos selected.
    document.addEventListener('shopify:section:select', (event) => { if (event.target.contains(root)) hold('editor-section', true); }, { signal });
    document.addEventListener('shopify:section:deselect', (event) => { if (event.target.contains(root)) hold('editor-section', false); }, { signal });
    document.addEventListener('shopify:block:select', (event) => { if (group.contains(event.target)) { hold('editor-block', true); reveal(event.target); } }, { signal });
    document.addEventListener('shopify:block:deselect', (event) => { if (group.contains(event.target)) hold('editor-block', false); }, { signal });

    const resized = new ResizeObserver(() => layout());
    onChange(reduceMotion, () => { render(); layout(); }, signal);
    // A different device pixel ratio (browser zoom, another screen) changes the snap.
    let ratioQuery = null;
    const onRatio = () => { watchRatio(); layout(); };
    function watchRatio() {
      if (ratioQuery && typeof ratioQuery.removeEventListener === 'function') ratioQuery.removeEventListener('change', onRatio);
      ratioQuery = window.matchMedia(`(resolution: ${dpr()}dppx)`);
      onChange(ratioQuery, onRatio, signal);
    }
    watchRatio();

    // Start once the section is near the viewport, the fonts have settled (names change the loop
    // width) and every real mark has decoded. The rail would clip lazy images that never load, so
    // they are switched to eager first.
    const settle = (promise, ms) => Promise.race([Promise.resolve(promise).catch(() => null), new Promise((resolve) => setTimeout(resolve, ms))]);
    const start = async () => {
      const images = [...group.querySelectorAll('img')];
      images.forEach((img) => { img.loading = 'eager'; });
      await Promise.all([
        settle(document.fonts && document.fonts.ready, 3000),
        ...images.map((img) => img.decode().catch(() => null)),
      ]);
      if (destroyed) return;
      started = true;
      if (moving()) {
        // Build the copies (and let them decode) before the first frame moves.
        layout();
        await Promise.all([...track.querySelectorAll('[data-proof-rail-clone] img')].map((img) => img.decode().catch(() => null)));
        if (destroyed) return;
      }
      viewport.scrollLeft = 0;
      root.setAttribute('data-ready', '');
      holds.delete('starting');
      sync();
      resized.observe(viewport);
      resized.observe(group);
      render();
      layout();
    };
    const approach = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      approach.disconnect();
      start();
    }, { rootMargin: '600px 0px' });
    approach.observe(root);

    render();
    return {
      destroy() {
        destroyed = true;
        abort.abort();
        onscreen.disconnect();
        approach.disconnect();
        resized.disconnect();
        if (anim) anim.cancel();
        anim = null;
        track.querySelectorAll('[data-proof-rail-clone]').forEach((node) => node.remove());
        for (const name of ['data-ready', 'data-paused', ...HOLDS.map((h) => `data-${h}`)]) root.removeAttribute(name);
        root.style.removeProperty('--pl-round');
      },
    };
  }

  const roots = (scope) => {
    if (!scope || !scope.querySelectorAll) return [];
    return [...(scope.matches && scope.matches(SELECTOR) ? [scope] : []), ...scope.querySelectorAll(SELECTOR)];
  };
  function init(scope = document) {
    for (const root of roots(scope)) {
      if (controllers.has(root)) continue;
      const controller = setup(root);
      if (controller) controllers.set(root, controller);
    }
  }
  function destroy(scope = document) {
    for (const root of roots(scope)) {
      const controller = controllers.get(root);
      if (controller) { controller.destroy(); controllers.delete(root); }
    }
  }

  window.akinciProofRail = { init, destroy };
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
})();
